// src/components/ParticipantTracker/Modals/SiteReportModal.tsx
// PDF-ready site report for Regeneron/Duke clinical stakeholders

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Modal, ModalOverlay, ModalContent } from '@chakra-ui/react';
import { fromUnixTime, addDays } from 'date-fns';
import { ClinicDevice, CombinedApiData, DeviceMap, LayoutCountMap, RetinalImageCounts, VvpResult, getParticipantAnchorValue } from '../interfaces/participantTrackerTypes';
import { BundleStatusInfo, BundleData, BundleState, OverallBundleStatus } from '../interfaces/bundleTypes';
import { useProtocolConfigContext } from '../ProtocolConfigContext';
import { useAppStore } from '@/stores/app';
import { usePharma } from '@/providers/PharmaProvider';
import { REQUIRED_IMAGES } from '../Cells/ImagesStatusCell';
import {
  BundleMilestone,
  ProtocolConfig,
  getBundleMilestoneExpectedDay,
  getBundleMilestoneMinimumValidTests,
  getBundleMilestoneProtocolDay,
  getBundleMilestoneTargetSessions,
  getBundleMilestoneWindowDaysAfter,
  getBundleMilestoneWindowDaysBefore,
  getBundleMilestoneWindowEndDay,
  getBundleMilestoneWindowStartDay,
} from '../types/protocolConfig';
import type { SptDataSource } from '../types';
import {
  differenceInETCalendarDays,
  formatDisplayDateShort,
  parseApiDate,
  parseUtcDateTime,
  formatTimestampET,
} from '../utils/dateUtils';
import {
  calculateBundleStatus,
  rebucketHomeTestsToVisitWindows,
  countTestsInWindow,
  getTestType,
  isOutlierResult,
  isHomeResult,
  isArchivedResult,
  getBundleStartWindowDays,
  getCollectionWindowDays,
  getBundleComplianceCloseInfo,
} from '../utils/bundleUtils';
import { getActionableInfo } from '../utils/participantUtils';
import { ReportTooltip } from '../ReportTooltip';

// ============================================================
// TYPES
// ============================================================

interface SiteReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  participants: CombinedApiData[];
  bundleStatusMap: Map<string, BundleStatusInfo>;
  deviceMap: DeviceMap;
  layoutMap: LayoutCountMap;
}

export interface BundleStatusReportProps {
  participants: CombinedApiData[];
  bundleStatusMap: Map<string, BundleStatusInfo>;
  deviceMap: DeviceMap;
  layoutMap: LayoutCountMap;
  onClose?: () => void;
  renderMode?: 'modal' | 'page' | 'print';
  enableExpandableRows?: boolean;
  isUatEligible?: boolean;
  sptSource?: SptDataSource;
  onSptSourceChange?: (source: SptDataSource) => void;
  /** Called when the user clicks Refresh — re-fetches all API data */
  onRefetchData?: () => void;
  /** Hide the site filter input (e.g. when data is already filtered to a single site) */
  hideSiteFilter?: boolean;
  /** Human-readable site scope when the report data is already filtered upstream */
  siteScopeLabel?: string;
}

type B1WindowMode = 'exact' | 'visit_window';
type QualityRating = 'Great' | 'Good' | 'Fair' | 'Mixed' | 'Poor' | 'At Risk' | 'Issue';
type ReportTab = 'randomized' | 'excluded' | 'screening';
type CohortFilter = 'All' | 'A' | 'B';
type ParticipantCategory = ReportTab;
type BundleTestCount = { total: number; outliers: number; reliable: number };
type BundleCountTone = 'missed' | 'complete' | 'usable' | 'default';
type SortableColumn = 'participant' | 'investigator' | 'device' | 'randDate' | 'lastLogin' | 'next' | 'nextWindow' | 'sv2Window' | 'sv3Window';
type SortDirection = 'asc' | 'desc';
interface SortConfig { column: SortableColumn; direction: SortDirection }
const DEFAULT_SORT: SortConfig = { column: 'randDate', direction: 'asc' };
const SORTABLE_COLUMN_MAP: Record<string, SortableColumn | undefined> = {
  Participant: 'participant',
  'Investigator Name': 'investigator',
  Device: 'device',
  'Rand Date': 'randDate',
  'SV2 Window': 'sv2Window',
  'SV3 (Baseline)': 'sv3Window',
  'Last Login': 'lastLogin',
  Next: 'next',
  'Next Start Window': 'nextWindow',
};

interface ComplianceStats {
  actual: number;
  expected: number;
  percent: number | null;
}

interface CohortComplianceSummary {
  /** Display label for the KPI row: 'Cohort A' / 'Cohort B', or 'Overall' for cohort-less studies */
  cohort: string;
  overall: ComplianceStats;
  baseline: ComplianceStats;
}

const TAB_LABELS: Record<ReportTab, string> = {
  randomized: 'Randomized',
  excluded: 'Screen Failed / Not Participating',
  screening: 'Screening',
};

const REPORT_TAB_ORDER: ReportTab[] = ['randomized', 'screening', 'excluded'];
const COHORT_B_START = new Date('2026-04-15T00:00:00-04:00');
const B1_TINT = '#f1f5f9';        // slate-100
const B1_TINT_BORDER = '#cbd5e1'; // slate-300

const getReportConfig = (base: ProtocolConfig, b1Mode: B1WindowMode): ProtocolConfig => ({
  ...base,
  bundles: {
    ...base.bundles,
    milestones: base.bundles.milestones.map(ms => {
      // The B1 window-mode toggle is a SIENNA display concept; a pre-anchor
      // baseline (DFI) keeps its ruleset window untouched — stomping
      // windowDaysBefore/collectionAnchor here broke its strip and bucketing.
      if (ms.bundleNumber !== 1 || ms.windowMode === 'pre_anchor') return ms;
      return {
        ...ms,
        windowDaysBefore: 0,
        windowDaysAfter: b1Mode === 'visit_window' ? 28 : 0,
        collectionAnchor: b1Mode === 'visit_window' ? 'first_test' as const : 'expected_day' as const,
      };
    }),
  },
});

interface BundleDescription {
  text: string;
  missed: boolean;
  hasIssue: boolean;
}

interface TestLogEntry {
  timestamp: number;
  dateTime: string; // "26-Mar-2026 3:15 PM ET"
  type: 'Training' | 'Localizer' | 'Home' | 'Unknown';
  bundleNumber: string;
  isOutlier: boolean;
  isArchived: boolean;
}

interface ReportParticipant {
  participant: CombinedApiData;
  bundleStatus: BundleStatusInfo | null;
  randDate: Date | null;
  baselineDate: Date | null;
  category: ParticipantCategory;
  cohort: 'A' | 'B' | null;
  siteLabel: string;
  participantId: string;
  deviceId: string;
  lastLogin: string;
  lastLoginDate: Date | null;
  participantCompliance: ComplianceStats;
  quality: QualityRating;
  onboardingNotes: string;
  bundleDescriptions: BundleDescription[];
  bundleTestCounts: BundleTestCount[];
  layoutDays: LayoutDayInfo;
  nextBundle: string;
  nextBundleNumber: number;
  nextWindowDate: Date | null;
  nextWindow: React.ReactNode;
  actionFlags: string;
  testLog: TestLogEntry[];
  index: number;
}

// ============================================================
// STYLES
// ============================================================


/** Format date as "27-Mar-2026" (day-month-year, clinical standard) */
const fmtDateDMY = (date: Date | null | undefined): string => {
  if (!date) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    timeZone: 'America/New_York',
  }).format(date).replace(/ /g, '-');
};

const fmtTimeET = (date: Date | null | undefined): string => {
  if (!date) return '—';
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'America/New_York',
  }).format(date);
};

const fmtDateTimeDMY = (date: Date | null | undefined): string => {
  if (!date) return '—';
  return `${fmtDateDMY(date)} ${fmtTimeET(date)} ET`;
};

const getProtocolDayFromOffset = (dayOffset: number): number => {
  return dayOffset >= 0 ? dayOffset + 1 : dayOffset;
};

const getOffsetFromProtocolDay = (protocolDay: number): number => {
  return protocolDay > 0 ? protocolDay - 1 : protocolDay;
};

const formatProtocolDay = (protocolDay: number): string => {
  return `D${protocolDay}`;
};

const formatProtocolDayFromOffset = (dayOffset: number): string => {
  return formatProtocolDay(getProtocolDayFromOffset(dayOffset));
};

const addProtocolDays = (baselineDate: Date, protocolDay: number): Date => {
  return addDays(baselineDate, getOffsetFromProtocolDay(protocolDay));
};

const getParticipantIdentifier = (participant: CombinedApiData): string => {
  return participant.Subject || participant.patient_user_name || '—';
};

const getParticipantSitePrefix = (participant: CombinedApiData): string => {
  const id = getParticipantIdentifier(participant).replace(/\D/g, '');
  return id.length >= 6 ? id.slice(0, 6) : '';
};

const getSiteLabel = (participant: CombinedApiData): string => {
  return participant['Site Number'] || getParticipantSitePrefix(participant) || participant.clinic_name || '—';
};

const getParticipantCategory = (participant: CombinedApiData): ParticipantCategory => {
  const status = participant.Status?.toLowerCase() || '';
  if (
    participant['Screen Failed'] === 'YES' ||
    status.includes('screen failed') ||
    status.includes('not participating') ||
    status.includes('withdrawn')
  ) {
    return 'excluded';
  }

  if (!parseApiDate(getParticipantAnchorValue(participant)) || status.includes('screen')) {
    return 'screening';
  }

  return 'randomized';
};

/** Cohort B start from the ruleset (falls back to the legacy SIENNA constant) */
const getCohortBStart = (config: ProtocolConfig): Date =>
  config.cohorts?.bStartDate
    ? new Date(`${config.cohorts.bStartDate}T00:00:00-04:00`)
    : COHORT_B_START;

const getCohort = (randDate: Date | null, config: ProtocolConfig): 'A' | 'B' | null => {
  if (!config.cohorts) return null; // study has no cohort concept (e.g. DFI18231)
  if (!randDate) return null;
  return randDate < getCohortBStart(config) ? 'A' : 'B';
};

const parseDeviceDate = (value: string | null | undefined): Date | null => {
  return parseUtcDateTime(value);
};

const getPrimaryDevice = (devices: ClinicDevice[]): ClinicDevice | null => {
  const devicesWithId = devices.filter(device => (
    device.device_id ||
    device.device_serial_number ||
    device.device_alias
  ));
  if (devicesWithId.length === 0) return null;
  return devicesWithId.find(device => device.home_device_is_active === '1') || devicesWithId[0];
};

const getDeviceIdForReport = (devices: ClinicDevice[]): string => {
  const device = getPrimaryDevice(devices);
  return device?.device_alias || device?.device_id || device?.device_serial_number || '—';
};

const getLastLoginDateForReport = (devices: ClinicDevice[]): Date | null => {
  return devices
    .map(device => parseDeviceDate(device.home_device_time_last_login))
    .filter((date): date is Date => Boolean(date))
    .sort((a, b) => b.getTime() - a.getTime())[0] ?? null;
};

const formatCompliance = (stats: ComplianceStats): string => {
  if (stats.percent === null) return '-';
  return `${stats.percent}%`;
};

const sortReportParticipants = (a: ReportParticipant, b: ReportParticipant): number => {
  const tA = a.randDate ? a.randDate.getTime() : Infinity;
  const tB = b.randDate ? b.randDate.getTime() : Infinity;
  if (tA !== tB) return tA - tB;
  return a.participantId.localeCompare(b.participantId);
};

const buildSortComparator = (config: SortConfig) => (a: ReportParticipant, b: ReportParticipant): number => {
  // Projected SV2/SV3 windows sort by their projected first day, independent of
  // rand date: screening rows have no rand date but DO carry projected windows,
  // so this must run before the no-rand-date pin below or the sort is ignored.
  if (config.column === 'sv2Window' || config.column === 'sv3Window') {
    const field = config.column === 'sv2Window' ? 'Projected Visit 2 First Day' : 'Projected Baseline First Day';
    const dA = parseApiDate(a.participant[field]);
    const dB = parseApiDate(b.participant[field]);
    if (!dA && !dB) return a.participantId.localeCompare(b.participantId);
    if (!dA) return 1;   // nulls always sort last, regardless of direction
    if (!dB) return -1;
    let cmp = dA.getTime() - dB.getTime();
    if (config.direction === 'desc') cmp = -cmp;
    if (cmp === 0) cmp = a.participantId.localeCompare(b.participantId);
    return cmp;
  }

  // BSR.04: no-rand-date rows always pin to bottom
  const aHas = a.randDate !== null;
  const bHas = b.randDate !== null;
  if (aHas !== bHas) return aHas ? -1 : 1;
  if (!aHas && !bHas) return a.participantId.localeCompare(b.participantId);

  let cmp = 0;
  switch (config.column) {
    case 'participant':
      cmp = a.participantId.localeCompare(b.participantId);
      break;
    case 'investigator':
      cmp = (a.participant.Investigator ?? '').localeCompare(b.participant.Investigator ?? '');
      break;
    case 'device':
      cmp = a.deviceId.localeCompare(b.deviceId);
      break;
    case 'randDate':
      cmp = a.randDate!.getTime() - b.randDate!.getTime();
      break;
    case 'lastLogin': {
      const lA = a.lastLoginDate?.getTime() ?? -Infinity;
      const lB = b.lastLoginDate?.getTime() ?? -Infinity;
      cmp = lA - lB;
      break;
    }
    case 'next':
      cmp = a.nextBundleNumber - b.nextBundleNumber;
      break;
    case 'nextWindow': {
      const wA = a.nextWindowDate?.getTime() ?? Infinity;
      const wB = b.nextWindowDate?.getTime() ?? Infinity;
      cmp = wA - wB;
      break;
    }
  }
  if (config.direction === 'desc') cmp = -cmp;
  if (cmp === 0) cmp = a.participantId.localeCompare(b.participantId);
  return cmp;
};

interface LayoutDayInfo {
  training: string[];  // e.g. ["D-18"]
  localizer: string[]; // e.g. ["D-15", "D-14"]
  home: string[];      // e.g. ["D1"]
}

/** Collect all layout creation dates per type as protocol-day offsets from baseline.
 *  Falls back to absolute dates if no baseline is available. */
const getLayoutDays = (layoutMap: LayoutCountMap, patientId: string | null, baselineDate: Date | null): LayoutDayInfo => {
  const result: LayoutDayInfo = { training: [], localizer: [], home: [] };
  if (!patientId) return result;
  const ld = layoutMap[patientId];
  if (!ld?.layouts_json) return result;

  for (const layout of ld.layouts_json) {
    const name = (layout.name || '').toLowerCase();
    const dateStr = layout.timestamp || layout.last_modified;
    if (!dateStr) continue;
    const date = parseUtcDateTime(dateStr);
    if (!date) continue;

    let label: string;
    if (baselineDate) {
      const dayOffset = differenceInETCalendarDays(date, baselineDate);
      label = formatProtocolDayFromOffset(dayOffset);
    } else {
      label = fmtDateDMY(date);
    }

    if (name.includes('training')) result.training.push(label);
    else if (name.includes('localizer')) result.localizer.push(label);
    else if (name.includes('home')) result.home.push(label);
  }

  return result;
};

/**
 * Requirements color coding for bundle count cells (BSR.F09).
 * - Missed → rose (red)
 * - 10/10 → emerald (green)
 * - 6-9/10 after Collection Window close → light green
 * - Everything else → transparent (uncolored)
 */
const bundleCountColor = (
  total: number,
  missed = false,
  collectionWindowClosed = false,
  config: ProtocolConfig,
  milestone?: BundleMilestone,
): { bg: string; text: string; shouldColor: boolean; tone: BundleCountTone } => {
  const target = milestone
    ? getBundleMilestoneTargetSessions(config, milestone)
    : config.bundles.targetTestsPerBundle;
  const partialMin = milestone
    ? getBundleMilestoneMinimumValidTests(config, milestone)
    : config.bundles.completionTiers.partialMin;
  if (missed) return { bg: '#ffe4e6', text: '#9f1239', shouldColor: true, tone: 'missed' };
  if (total >= target) {
    return { bg: '#d1fae5', text: '#065f46', shouldColor: true, tone: 'complete' };
  }
  if (collectionWindowClosed && total >= partialMin) {
    return { bg: '#ecfccb', text: '#4d7c0f', shouldColor: true, tone: 'usable' };
  }
  return { bg: 'transparent', text: '#4b5563', shouldColor: false, tone: 'default' };
};

const testTypeColors = {
  training:  { dot: '#228B22' },
  localizer: { dot: '#4169E1' },
  home:      { dot: '#7c3aed' },
};


// ============================================================
// BUNDLE DESCRIPTION GENERATOR
// ============================================================

function describeBundleForReport(
  bundle: BundleData | undefined,
  milestone: BundleMilestone,
  baselineDate: Date | null,
  config: ProtocolConfig,
  testCounts: BundleTestCount,
  referenceDate: Date,
): BundleDescription {
  if (!bundle || bundle.testCount === 0) {
    // Check if window has passed
    if (!baselineDate) return { text: '', missed: false, hasIssue: false };
    const expectedDay = getBundleMilestoneExpectedDay(milestone);
    const windowEndDay = getBundleMilestoneWindowEndDay(config, milestone);
    const referenceDay = differenceInETCalendarDays(referenceDate, baselineDate);
    if (windowEndDay !== null) {
      if (referenceDay > windowEndDay) return { text: 'Missed', missed: true, hasIssue: true };
    }
    if (referenceDay < expectedDay) return { text: '', missed: false, hasIssue: false }; // Future
    return { text: '', missed: false, hasIssue: false };
  }

  const parts: string[] = [];
  const { total } = testCounts;
  const expectedDay = getBundleMilestoneExpectedDay(milestone);

  // Timing relative to window
  if (baselineDate && bundle.startDate) {
    const startDayFromBaseline = differenceInETCalendarDays(bundle.startDate, baselineDate);
    const windowStartDay = getBundleMilestoneWindowStartDay(config, milestone);
    const windowEndDay = getBundleMilestoneWindowEndDay(config, milestone);
    const delta = startDayFromBaseline - expectedDay;

    const protocolDay = getBundleMilestoneProtocolDay(milestone);
    const startProtocolDayLabel = formatProtocolDayFromOffset(startDayFromBaseline);
    if (milestone.bundleNumber === 1) {
      if (delta === 0) parts.push('Started on time');
      else if (delta > 0) parts.push(`Started ${startProtocolDayLabel}`);
      else parts.push(`Started ${startProtocolDayLabel} (before target)`);
    } else if (windowEndDay !== null && startDayFromBaseline >= windowStartDay && startDayFromBaseline <= windowEndDay) {
      if (delta === 0) parts.push('Within window');
      else if (delta < 0) parts.push(`Within window; started ${delta}d from anchor (Day ${protocolDay})`);
      else parts.push(`Within window; started +${delta}d from anchor (Day ${protocolDay})`);
    } else {
      parts.push(`Started ${startProtocolDayLabel} — outside window`);
    }
  }

  // Test counts
  const testCount = total > 0 ? total : bundle.testCount;
  if (testCount > 0) parts.push(`${testCount} tests`);

  // Status assessment for partial/incomplete
  if (bundle.state === BundleState.PARTIAL) {
    parts.push(`Incomplete — only ${total || bundle.testCount} tests within window`);
  }

  const text = parts.join('; ');
  const hasIssue = bundle.state === BundleState.EXPIRED || bundle.state === BundleState.EXPIRING;

  return { text, missed: false, hasIssue };
}

// ============================================================
// QUALITY RATING DERIVATION
// ============================================================

function deriveOverallQuality(bs: BundleStatusInfo | null, descriptions: BundleDescription[]): QualityRating {
  if (!bs) return 'Issue';

  const missedCount = descriptions.filter(d => d.missed).length;
  const issueCount = descriptions.filter(d => d.hasIssue).length;
  const completedBundles = bs.bundles.filter(b => b.state === BundleState.COMPLETE).length;
  const partialBundles = bs.bundles.filter(b => b.state === BundleState.PARTIAL).length;
  const activeBundles = bs.bundles.filter(b => b.state === BundleState.ACTIVE || b.state === BundleState.EXPIRING).length;

  if (bs.overallStatus === OverallBundleStatus.COMPLETE && missedCount === 0) return 'Great';
  if (completedBundles >= bs.expectedByNow && missedCount === 0 && issueCount === 0) return 'Good';
  if (bs.overallStatus === OverallBundleStatus.AT_RISK || bs.overallStatus === OverallBundleStatus.EXPIRING) return 'At Risk';
  if (missedCount >= 2) return 'Poor';
  if (missedCount === 1 && partialBundles > 0) return 'Poor';
  if (missedCount === 1) return 'Mixed';
  if (partialBundles > 0 && completedBundles > 0) return 'Mixed';
  if (bs.overallStatus === OverallBundleStatus.BEHIND || bs.overallStatus === OverallBundleStatus.STALE) return 'Issue';
  if (partialBundles > 0 || issueCount > 0) return 'Fair';
  if (activeBundles > 0) return 'Good';
  return 'Good';
}

// ============================================================
// ONBOARDING NOTES GENERATOR
// ============================================================

function generateOnboardingNotes(p: CombinedApiData, baselineDate: Date | null, config: ProtocolConfig): string {
  const parts: string[] = [];

  // Localizer info
  const locTs = p.localizer_test_unix_times || [];
  if (locTs.length > 0) {
    const dates = locTs.map(ts => formatTimestampET(ts, 'dateShort'));
    if (locTs.length > 1) {
      parts.push(`${locTs.length} localizer tests (${dates.join(', ')})`);
    }
  }

  // First home test timing
  const homeTs = p.home_test_unix_times || [];
  if (homeTs.length > 0 && baselineDate) {
    const firstHome = fromUnixTime(Math.min(...homeTs));
    const dayFromBaseline = differenceInETCalendarDays(firstHome, baselineDate);
    const firstWindowEnd = getBundleMilestoneWindowEndDay(config, config.bundles.milestones[0]);
    if (firstWindowEnd !== null && dayFromBaseline > firstWindowEnd) {
      parts.push(`First home test on day ${dayFromBaseline} — did not align with any window`);
    } else if (dayFromBaseline < 0) {
      parts.push(`First home test ${Math.abs(dayFromBaseline)}d before rand date`);
    }
  }

  // Rand date discrepancy
  const randDate = parseApiDate(getParticipantAnchorValue(p));
  const sv3 = parseApiDate(p.actual_sv3_date);
  if (randDate && sv3) {
    const delta = differenceInETCalendarDays(sv3, randDate);
    if (Math.abs(delta) > 7) {
      parts.push(`1st home test ${delta > 0 ? '+' : ''}${delta}d from rand date`);
    }
  }

  if (p.Comments) {
    parts.push(p.Comments);
  }

  return parts.length > 0 ? parts.join('. ') + '.' : 'N/A';
}

// ============================================================
// ACTION FLAGS GENERATOR
// ============================================================

function generateActionFlags(
  bs: BundleStatusInfo | null,
  descriptions: BundleDescription[],
): string {
  if (!bs) return '';
  const flags: string[] = [];

  // Missed bundles
  const missedNums = descriptions
    .map((d, i) => d.missed ? `B${i + 1}` : null)
    .filter(Boolean) as string[];
  if (missedNums.length > 1) {
    flags.push(`${missedNums.join(' & ')} both missed`);
  } else if (missedNums.length === 1) {
    flags.push(`${missedNums[0]} missed`);
  }

  // Expiring bundle
  const expiringBundle = bs.bundles.find(b => b.state === BundleState.EXPIRING);
  if (expiringBundle) {
    flags.push(`URGENT: B${expiringBundle.bundleNumber} expiring${expiringBundle.daysRemaining !== null ? ` in ${expiringBundle.daysRemaining}d` : ''}`);
  }

  // Incomplete bundles
  const incomplete = bs.bundles.filter(b => b.state === BundleState.PARTIAL);
  for (const b of incomplete) {
    flags.push(`B${b.bundleNumber} incomplete`);
  }

  return flags.join('; ');
}

// ============================================================
// CALENDAR & TEST LOG HELPERS
// ============================================================

const buildTestLog = (p: CombinedApiData, bs: BundleStatusInfo | null): TestLogEntry[] => {
  if (!p.vvp_results || p.vvp_results.length === 0) return [];

  // Build reverse map: timestamp → rebucketed bundle number
  const tsToBundleNum = new Map<number, number>();
  if (bs) {
    for (const b of bs.bundles) {
      for (const ts of b.testTimestamps) {
        tsToBundleNum.set(ts, b.bundleNumber);
      }
    }
  }

  return p.vvp_results
    .map(r => {
      const ts = parseInt(r.test_unix_time, 10);
      if (isNaN(ts)) return null;
      const rebucketedNum = tsToBundleNum.get(ts);
      const d = fromUnixTime(ts);
      return {
        timestamp: ts,
        dateTime: fmtDateTimeDMY(d),
        type: getTestType(r),
        bundleNumber: rebucketedNum !== undefined ? `B${rebucketedNum}` : '—',
        isOutlier: isOutlierResult(r),
        isArchived: isArchivedResult(r),
      };
    })
    .filter((x): x is TestLogEntry => x !== null)
    .sort((a, b) => a.timestamp - b.timestamp);
};

const calculateParticipantCompliance = (
  rd: Pick<ReportParticipant, 'randDate' | 'bundleStatus' | 'bundleTestCounts'>,
  config: ProtocolConfig,
  referenceDate: Date,
  bundleNumbers?: number[],
): ComplianceStats => {
  const randDate = rd.randDate;
  if (!randDate) return { actual: 0, expected: 0, percent: null };

  let actual = 0;
  let expected = 0;
  config.bundles.milestones.forEach((milestone, index) => {
    if (bundleNumbers && !bundleNumbers.includes(milestone.bundleNumber)) return;
    const bundle = rd.bundleStatus?.bundles.find(b => b.bundleNumber === milestone.bundleNumber);
    const { closeDate } = getBundleComplianceCloseInfo(config, milestone, bundle, randDate);
    // ET-calendar-day compare: closeDate may be an ET-noon-anchored boundary
    // (see parseApiDate), so instant comparison would flip mid-day.
    if (!closeDate || differenceInETCalendarDays(referenceDate, closeDate) < 0) return;

    const milestoneTarget = getBundleMilestoneTargetSessions(config, milestone);
    expected += milestoneTarget;
    actual += Math.min(rd.bundleTestCounts[index]?.total || 0, milestoneTarget);
  });

  return {
    actual,
    expected,
    percent: expected > 0 ? Math.round((actual / expected) * 100) : null,
  };
};

const buildCohortComplianceSummary = (
  rows: ReportParticipant[],
  config: ProtocolConfig,
  referenceDate: Date,
): CohortComplianceSummary[] => {
  // Cohort-less studies (e.g. DFI18231) get a single Overall row
  const cohortKeys: ('A' | 'B' | null)[] = config.cohorts ? ['A', 'B'] : [null];
  return cohortKeys.map(cohortKey => {
    const cohortRows = cohortKey === null ? rows : rows.filter(row => row.cohort === cohortKey);
    const sum = (bundleNumbers?: number[]): ComplianceStats => {
      const totals = cohortRows.reduce(
        (acc, row) => {
          const stats = calculateParticipantCompliance(row, config, referenceDate, bundleNumbers);
          return {
            actual: acc.actual + stats.actual,
            expected: acc.expected + stats.expected,
          };
        },
        { actual: 0, expected: 0 }
      );

      return {
        ...totals,
        percent: totals.expected > 0 ? Math.round((totals.actual / totals.expected) * 100) : null,
      };
    };

    return {
      cohort: cohortKey === null ? 'Overall' : `Cohort ${cohortKey}`,
      overall: sum(),
      baseline: sum([1]),
    };
  });
};

const formatCompliancePercent = (stats: ComplianceStats): string => (
  stats.percent === null ? '—' : `${stats.percent}%`
);

const formatComplianceFraction = (stats: ComplianceStats): string => (
  stats.expected > 0 ? `${stats.actual} / ${stats.expected}` : '—'
);

const ComplianceKpiRow: React.FC<{
  cohort: string;
  stats: ComplianceStats;
}> = ({ cohort, stats }) => {
  const pct = stats.percent ?? 0;
  const hasData = stats.expected > 0;
  const isEmpty = !hasData || pct === 0;

  return (
    <div className="flex h-6 items-center gap-2 text-[10px]">
      <span className="w-14 shrink-0 font-semibold leading-none text-gray-700">
        {cohort}
      </span>
      <span
        className={`w-10 shrink-0 text-right text-[16px] font-bold leading-none ${
          'text-gray-900'
        }`}
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {formatCompliancePercent(stats)}
      </span>
      <span
        className={`w-16 shrink-0 text-right font-mono text-[10px] leading-none ${
          'text-gray-500'
        }`}
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {formatComplianceFraction(stats)}
      </span>
      <div className="h-1.5 w-[180px] shrink-0 overflow-hidden rounded-full bg-gray-200">
        <div
          className="h-full rounded-full"
          style={{
            width: `${Math.min(100, Math.max(0, pct))}%`,
            background: '#1d4ed8',
          }}
        />
      </div>
    </div>
  );
};

const ComplianceKpiCard: React.FC<{
  title: string;
  detail: string | string[];
  summaries: CohortComplianceSummary[];
  getStats: (summary: CohortComplianceSummary) => ComplianceStats;
  variant?: 'neutral' | 'b1';
}> = ({ title, detail, summaries, getStats, variant = 'neutral' }) => {
  const isB1 = variant === 'b1';
  return (
    <div
      className="h-[88px] px-3 py-2"
      style={{ background: isB1 ? B1_TINT : '#fff' }}
    >
      <div className="mb-1">
        <ReportTooltip title={title} detail={detail} tone="info" placement="top">
          <span className="text-[10px] font-bold uppercase tracking-wide text-gray-700">
            {title}
          </span>
        </ReportTooltip>
      </div>
      <div className="flex flex-col gap-1">
        {summaries.map(summary => (
          <ComplianceKpiRow
            key={summary.cohort}
            cohort={summary.cohort}
            stats={getStats(summary)}
          />
        ))}
      </div>
    </div>
  );
};

const ComplianceKpiStrip: React.FC<{
  summaries: CohortComplianceSummary[];
  isSiteScoped?: boolean;
}> = ({ summaries, isSiteScoped = false }) => (
  <div className="sr-compliance-strip grid shrink-0 overflow-hidden rounded-lg border border-gray-200 grid-cols-2">
    <div className="border-r border-gray-200">
      <ComplianceKpiCard
        title="Overall test compliance"
        detail={isSiteScoped
          ? ["Across randomized Participants at this Site with at least one closed Collection Window.", "Compliance = actual home tests / expected (10 per closed bundle), rounded to whole percent."]
          : ["Across all randomized Participants with at least one closed Collection Window.", "Compliance = actual home tests / expected (10 per closed bundle), rounded to whole percent."]}
        summaries={summaries}
        getStats={summary => summary.overall}
      />
    </div>
    <div>
      <ComplianceKpiCard
        title="B1 (Baseline) test compliance"
        detail={['Same formula as Overall, restricted to Bundle 1 only.', 'B1 Start Window is Day 1 only — missed if no tests taken on Day 1.']}
        summaries={summaries}
        getStats={summary => summary.baseline}
        variant="b1"
      />
    </div>
  </div>
);

const buildReportTopSection = ({
  config,
  tabCounts,
  activeTab,
  onTabChange,
  cohortFilter,
  onCohortChange,
  siteFilter,
  onSiteFilterChange,
  siteOptions,
  participantFilter,
  onParticipantFilterChange,
  onClearFilters,
  sortConfig,
  complianceSummaries,
  lastRefresh,
  onRefresh,
  onPrint,
  isUatEligible = false,
  sptSource = 'production',
  onSptSourceChange,
  hideSiteFilter = false,
  siteScopeLabel,
}) => {
  const isDefaultSort = sortConfig.column === DEFAULT_SORT.column && sortConfig.direction === DEFAULT_SORT.direction;
  const hasFilters = (!hideSiteFilter && siteFilter !== 'All') || participantFilter || cohortFilter !== 'All' || !isDefaultSort;
  const lastRefreshLabel = fmtDateTimeDMY(lastRefresh);
  const printFilterSummary = [
    TAB_LABELS[activeTab],
    cohortFilter !== 'All' ? `Cohort ${cohortFilter}` : null,
    !hideSiteFilter && siteFilter !== 'All' ? `Site: ${siteFilter}` : null,
    participantFilter ? `Participant: ${participantFilter}` : null,
  ].filter(Boolean).join(' · ');

  const header = (
    <div className="mb-4 space-y-2.5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-base font-bold text-gray-900">
            {config.metadata.studyName} &mdash; VVP Bundle Status Report
            {siteScopeLabel ? ` (${siteScopeLabel})` : ''}
            {sptSource === 'uat' && (
              <span className="ml-2 inline-block rounded bg-amber-100 border border-amber-300 px-1.5 py-0.5 text-[9px] font-bold uppercase text-amber-800 align-middle">
                UAT Data
              </span>
            )}
          </div>
          <div className="mt-0.5 text-[10px] text-gray-500">
            Protocol {config.metadata.protocolId}
            {config.metadata.rulesetStatus && config.metadata.rulesetStatus !== 'approved' && (
              <span className="ml-1.5 inline-block rounded bg-orange-100 border border-orange-300 px-1 py-px text-[8px] font-bold uppercase text-orange-700 align-middle">
                {config.metadata.rulesetStatus} protocol ruleset
              </span>
            )}
          </div>
          <div className="sr-print-summary mt-1 text-[9px] font-semibold text-gray-500">
            {printFilterSummary} · Generated {lastRefreshLabel}
          </div>
        </div>
        <div className="flex items-center gap-2 text-[10px] text-gray-500 shrink-0">
          <span>Last refresh: <span className="font-mono">{lastRefreshLabel}</span></span>
          <button
            type="button"
            className="sr-toolbar h-7 rounded-md border border-gray-300 bg-white px-2 text-[11px] font-semibold text-gray-700 hover:bg-gray-100"
            onClick={onRefresh}
          >
            Refresh
          </button>
          {onPrint && (
            <button
              type="button"
              className="sr-toolbar h-7 rounded-md border border-blue-900 bg-blue-900 px-2.5 text-[11px] font-semibold text-white hover:bg-blue-800"
              onClick={onPrint}
            >
              Print
            </button>
          )}
        </div>
      </div>

      <div className="sr-tab-bar flex flex-wrap items-end gap-5 border-b border-gray-200 pt-0.5">
        {REPORT_TAB_ORDER.map(tab => (
          <button
            key={tab}
            type="button"
            className={`relative -mb-px pb-1.5 text-[11px] font-semibold transition-colors ${
              activeTab === tab ? 'text-blue-900' : 'text-gray-500 hover:text-gray-800'
            }`}
            onClick={() => onTabChange(tab)}
          >
            <span>{TAB_LABELS[tab]}</span>
            <span className="ml-1 font-mono text-[10px] text-gray-400">({tabCounts[tab]})</span>
            {activeTab === tab ? (
              <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-blue-900" />
            ) : null}
          </button>
        ))}
      </div>

      {sptSource === 'uat' && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
          <strong>UAT data source active.</strong> Results below are from the test spreadsheet, not production.
        </div>
      )}
    </div>
  );

  const filterBar = (
    <div className="sr-report-filter-bar flex gap-2.5 items-stretch mb-2.5" style={{ minWidth: '1280px' }}>
      {/* Left: filter controls */}
      <div className="sr-filter-controls flex-1 flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5">
        {config.cohorts && (
          <div className="sr-cohort-filter flex items-center gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">Cohort</span>
            <div className="flex h-7 overflow-hidden rounded-md border border-gray-300 bg-white">
              {(['All', 'A', 'B'] as const).map((cohort, index) => (
                <button
                  key={cohort}
                  type="button"
                  className={`px-2.5 text-[11px] font-semibold ${index > 0 ? 'border-l border-gray-300' : ''} ${
                    cohortFilter === cohort ? 'bg-blue-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                  onClick={() => onCohortChange(cohort)}
                >
                  {cohort}
                </button>
              ))}
            </div>
          </div>
        )}

        {isUatEligible && onSptSourceChange && (
          <div className="sr-source-filter flex items-center gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">Source</span>
            <div className="flex h-7 overflow-hidden rounded-md border border-gray-300 bg-white">
              {(['production', 'uat'] as const).map((src, index) => (
                <button
                  key={src}
                  type="button"
                  className={`px-2.5 text-[11px] font-semibold ${index > 0 ? 'border-l border-gray-300' : ''} ${
                    sptSource === src ? 'bg-blue-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                  onClick={() => onSptSourceChange(src)}
                >
                  {src === 'production' ? 'Prod' : 'UAT'}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="sr-filter-divider h-5 w-px bg-gray-300" />

        {!hideSiteFilter && (
          <input
            className="sr-site-filter h-7 w-[100px] rounded-md border border-gray-300 bg-white px-2 text-[11px]"
            value={siteFilter === 'All' ? '' : siteFilter}
            onChange={event => onSiteFilterChange(event.target.value || 'All')}
            placeholder="Site"
            autoComplete="off"
          />
        )}
        <input
          className="sr-participant-filter h-7 w-[130px] rounded-md border border-gray-300 bg-white px-2 text-[11px]"
          value={participantFilter}
          onChange={event => onParticipantFilterChange(event.target.value)}
          placeholder="Participant ID"
        />
        <button
          type="button"
          className={`sr-reset-filter h-7 rounded-md border px-2 text-[11px] font-semibold ${
            hasFilters
              ? 'border-gray-300 bg-white text-gray-600 hover:text-gray-800'
              : 'border-transparent text-transparent pointer-events-none'
          }`}
          onClick={onClearFilters}
        >
          Reset
        </button>
      </div>
      {/* Right: compliance KPI cards */}
      <ComplianceKpiStrip summaries={complianceSummaries} isSiteScoped={hideSiteFilter} />
    </div>
  );

  return { header, filterBar };
};

// ============================================================
// TIMELINE SUB-COMPONENTS
// ============================================================

/** Dot descriptor for rendering inside a day cell */
interface DotInfo {
  color: string;
  outline: boolean;
  round: boolean;
  size: number;
  dimmed?: boolean;
}

interface AssignedBundleDayCount {
  bundleNumber: number;
  count: number;
}

const LegendItem: React.FC<{
  marker: React.ReactNode;
  label: string;
  title?: string;
  detail?: string | string[];
  tone?: 'default' | 'info' | 'warning' | 'danger';
}> = ({ marker, label, title, detail, tone = 'default' }) => {
  const content = (
    <span className="flex items-center gap-0.5">
      {marker}
      {label}
    </span>
  );

  return title ? (
    <ReportTooltip title={title} detail={detail} tone={tone} placement="bottom">
      {content}
    </ReportTooltip>
  ) : content;
};

/** Legend row displayed above the bundle timeline */
const TimelineLegend: React.FC = () => (
  <div className="flex items-center mb-1">
    <div className="w-[95px] shrink-0" />
    <div className="flex items-center gap-3 text-[8px] text-gray-500">
      <LegendItem marker={<span className="inline-block w-2 h-2 border-2 border-indigo-500 rounded-sm" />} label="Protocol target" title="Protocol target" detail="Expected visit day according to the protocol schedule." tone="info" />
      <LegendItem marker={<span className="inline-block w-2 h-2 bg-amber-100 border border-amber-300 rounded-sm" />} label="Bundle Start Window" title="Bundle Start Window" detail="Testing should begin within this window. Active from 7 days before through 28 days after the target day." tone="warning" />
      <LegendItem marker={<span className="inline-block w-2 h-2 bg-blue-100 border border-blue-300 rounded-sm" />} label="Collection Window (10d or 10 tests)" title="Collection Window" detail={['Opens when first home test is collected within a Bundle Start Window. Closes after 10 calendar days or 10 home tests.', 'Max 2 tests counted per day.']} tone="info" />
      <LegendItem marker={<span className="inline-block w-[5px] h-[5px]" style={{ background: testTypeColors.training.dot }} />} label="Training" />
      <LegendItem marker={<span className="inline-block w-[5px] h-[5px]" style={{ background: testTypeColors.localizer.dot }} />} label="Localizer" />
      <LegendItem marker={<span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: testTypeColors.home.dot }} />} label="Home test" />
      <LegendItem marker={<span className="inline-block w-[7px] h-[7px] rounded-full bg-transparent box-border" style={{ border: `1.5px solid ${testTypeColors.home.dot}` }} />} label="Outlier" title="Outlier" detail="Flagged by 1.5×IQR mean sensitivity deviation. Still included in compliance counts." tone="warning" />
      <LegendItem marker={<span className="inline-block w-1.5 h-1.5 rounded-full opacity-50" style={{ background: testTypeColors.home.dot }} />} label="Overflow (capped)" title="Overflow" detail={['3rd or more test in a day, or test outside of a Collection Window.', 'Not counted toward bundle compliance.']} tone="default" />
      <LegendItem marker={<span className="inline-block w-2 h-2 bg-gray-50 rounded-sm box-border" style={{ border: '1.5px solid #f97316' }} />} label={`Today (${formatDisplayDateShort(new Date())})`} title="Today" detail="Today's position on the protocol timeline." tone="info" />
    </div>
  </div>
);

/** Single day cell with dots for training/localizer/home tests */
const DayCell: React.FC<{
  dayNum: number;
  dateLabel: string;
  bundleLabel: string;
  nextBundleLabel?: string;
  cellSize: number;
  bg: string;
  border: string;
  testInfo: { total: number; outliers: number } | undefined;
  assignedOnDay: number;
  assignedBundleCounts: AssignedBundleDayCount[];
  isTarget: boolean;
  isToday: boolean;
  inStartWindow: boolean;
  inColl: boolean;
  inNextStartWindow: boolean;
  inNextColl: boolean;
  hasTr: boolean;
  hasLoc: boolean;
}> = ({
  dayNum,
  dateLabel,
  bundleLabel,
  nextBundleLabel,
  cellSize,
  bg,
  border,
  testInfo,
  assignedOnDay,
  assignedBundleCounts,
  isTarget,
  isToday,
  inStartWindow,
  inColl,
  inNextStartWindow,
  inNextColl,
  hasTr,
  hasLoc,
}) => {
  const homeTotal = testInfo ? testInfo.total : 0;
  const homeOutlier = testInfo ? testInfo.outliers : 0;
  const assigned = Math.min(homeTotal, assignedOnDay);
  const assignedOutliers = Math.min(homeOutlier, assigned);
  const assignedReliable = assigned - assignedOutliers;
  const overflow = Math.max(0, homeTotal - assigned);
  const isInAnyColl = inColl || inNextColl;
  const dimHome = !isInAnyColl || assigned >= 10;
  const countedBundleTotal = assignedBundleCounts.reduce((sum, item) => sum + item.count, 0);

  const dots: DotInfo[] = [];
  if (hasTr) dots.push({ color: testTypeColors.training.dot, outline: false, round: false, size: 4 });
  if (hasLoc) dots.push({ color: testTypeColors.localizer.dot, outline: false, round: false, size: 4 });
  for (let v = 0; v < assignedReliable; v++) dots.push({ color: testTypeColors.home.dot, outline: false, round: true, size: 4 });
  for (let o = 0; o < assignedOutliers; o++) dots.push({ color: testTypeColors.home.dot, outline: true, round: true, size: 4, dimmed: dimHome });
  for (let f = 0; f < overflow; f++) dots.push({ color: testTypeColors.home.dot, outline: false, round: true, size: 4, dimmed: true });

  const allHomeDimmed = assigned === 0 || dimHome;
  const formatHomeTests = (count: number) => `${count} home test${count === 1 ? '' : 's'}`;
  const countedTestDetails = (() => {
    if (homeTotal === 0) return [];

    if (countedBundleTotal === 0) {
      return [`${formatHomeTests(homeTotal)} not counted toward a bundle`];
    }

    if (assignedBundleCounts.length === 1) {
      const [{ bundleNumber, count }] = assignedBundleCounts;
      const countLabel = count === homeTotal
        ? formatHomeTests(count)
        : `${count} of ${formatHomeTests(homeTotal)}`;
      return [`${countLabel} counted in B${bundleNumber}`];
    }

    return [
      `${countedBundleTotal} of ${formatHomeTests(homeTotal)} counted`,
      ...assignedBundleCounts.map(({ bundleNumber, count }) => `${count} in B${bundleNumber}`),
    ];
  })();
  const currentWindowLabel = `Within ${bundleLabel}`;
  const nextWindowLabel = nextBundleLabel ? `Within ${nextBundleLabel}` : undefined;
  const tooltipDetail = [
    isToday ? 'Today' : '',
    ...countedTestDetails,
    isTarget ? `${bundleLabel} protocol visit target` : '',
    inColl ? `${currentWindowLabel} Collection Window` : '',
    !inColl && inStartWindow ? `${currentWindowLabel} Bundle Start Window` : '',
    inNextColl && nextWindowLabel ? `${nextWindowLabel} Collection Window` : '',
    !inNextColl && inNextStartWindow && nextWindowLabel ? `${nextWindowLabel} Bundle Start Window` : '',
    hasTr ? 'Training test' : '',
    hasLoc ? 'Localizer test' : '',
    homeOutlier > 0 ? `${homeOutlier} outlier${homeOutlier === 1 ? '' : 's'} included` : '',
    overflow > 0 ? `${overflow} overflow/capped` : '',
    homeTotal > 0 && !isInAnyColl ? 'Outside active Collection Window' : '',
  ].filter(Boolean);

  const cell = (
    <div
      className="relative box-border flex flex-col items-center justify-center gap-0.5"
      style={{ width: `${cellSize}px`, height: '16px', background: bg, border, borderRadius: '1px' }}
    >
      {dots.length === 0 ? null : dots.length > 3 ? (
        <div
          className="flex items-center justify-center text-[6px] font-extrabold leading-none"
          style={{ color: testTypeColors.home.dot, opacity: allHomeDimmed ? 0.5 : 1 }}
        >
          {dots.length}
        </div>
      ) : (
        dots.map((dot, i) => (
          <div
            key={i}
            className="box-border"
            style={{
              width: `${dot.size}px`,
              height: `${dot.size}px`,
              borderRadius: dot.round ? '50%' : '0px',
              background: dot.outline ? 'transparent' : dot.color,
              border: dot.outline ? `1px solid ${dot.color}` : 'none',
              opacity: dot.dimmed ? 0.5 : 1,
            }}
          />
        ))
      )}
    </div>
  );

  const tooltipTone = homeTotal > 0 && !isInAnyColl
    ? 'warning'
    : isToday || inStartWindow || inNextStartWindow || inColl || inNextColl || isTarget
    ? 'info'
    : 'default';

  return (
    <ReportTooltip title={`${dateLabel} \u00b7 ${formatProtocolDay(dayNum)}`} detail={tooltipDetail} tone={tooltipTone}>
      {cell}
    </ReportTooltip>
  );
};

/** Pre-baseline screening strip. The SV2 visit window is study-specific
 * (SIENNA: D-21 through D-14; DFI: D-7 through D-1 per Amended Protocol 02).
 * For pre-anchor baselines (DFI B1) every pre-D1 day is collectable, so the
 * whole strip tints lightly and the SV2 window strongly. */
const SV2ScreeningRow: React.FC<{
  baselineDate: Date;
  trainingTimestamps: number[];
  localizerTimestamps: number[];
  homeTimestamps: number[];
  toStudyDay: (ts: number) => number;
  cellSize: number;
  /** SV2 visit window in protocol-day offsets; defaults to SIENNA's D-21..D-14 */
  sv2Window?: { start: number; end: number };
  /** True when B1 is a pre-anchor baseline collected on this strip (DFI) */
  baselineCollectable?: boolean;
}> = ({ baselineDate, trainingTimestamps, localizerTimestamps, homeTimestamps, toStudyDay, cellSize, sv2Window, baselineCollectable = false }) => {
  const sv2WinStart = sv2Window?.start ?? -21;
  const sv2WinEnd = sv2Window?.end ?? -14;
  // SIENNA shows a 21-day pre-baseline runway (SV2 window D-21..D-14 plus the
  // gap to D1). When the SV2 window runs right up to D-1 (DFI: D-7..D-1), the
  // strip spans exactly that window — home tests can't predate SCR2, so the
  // earlier days would always be empty.
  const rowStart = sv2WinEnd === -1 ? sv2WinStart : -21;
  const rowEnd = -1;
  const PRE_BASELINE_DAYS = -rowStart;
  const sv2TrainingDays = new Set(trainingTimestamps.map(ts => toStudyDay(ts)));
  const sv2LocalizerDays = new Set(localizerTimestamps.map(ts => toStudyDay(ts)));
  const sv2HomeDays = new Map<number, number>();
  for (const ts of homeTimestamps) {
    const d = toStudyDay(ts);
    if (d >= rowStart && d <= rowEnd) sv2HomeDays.set(d, (sv2HomeDays.get(d) || 0) + 1);
  }
  const sv2StartDate = addProtocolDays(baselineDate, rowStart);
  const sv2EndDate = addProtocolDays(baselineDate, rowEnd);
  const sv2WindowEndDate = addProtocolDays(baselineDate, sv2WinEnd);

  return (
    <>
      <div className="flex items-center">
        <div className="w-[95px] shrink-0 flex items-center justify-end">
          <div className="text-[10px] font-bold font-mono text-gray-500 w-[22px]">SV2</div>
          <div className="text-[8px] text-gray-500 w-9 text-right font-mono">{formatProtocolDay(rowStart)}</div>
          <div className="text-right text-[7px] text-gray-400 ml-1 min-w-[25px]">{formatDisplayDateShort(sv2StartDate)}</div>
          <div className="ml-auto shrink-0" style={{ width: '6px', height: '1px', background: '#cbd5e188' }} />
        </div>
        <div className="flex gap-px">
          {Array.from({ length: PRE_BASELINE_DAYS }).map((_, di) => {
            const dayNum = rowStart + di;
            const hasTr = sv2TrainingDays.has(dayNum);
            const hasLoc = sv2LocalizerDays.has(dayNum);
            const homeCount = sv2HomeDays.get(dayNum) || 0;
            const inSv2Window = dayNum >= sv2WinStart && dayNum <= sv2WinEnd;

            const sv2Dots: { color: string; round: boolean; size: number }[] = [];
            if (hasTr) sv2Dots.push({ color: testTypeColors.training.dot, round: false, size: 4 });
            if (hasLoc) sv2Dots.push({ color: testTypeColors.localizer.dot, round: false, size: 4 });
            for (let h = 0; h < Math.min(homeCount, 3 - sv2Dots.length); h++) {
              sv2Dots.push({ color: testTypeColors.home.dot, round: true, size: 4 });
            }

            const dateLabel = formatDisplayDateShort(addProtocolDays(baselineDate, dayNum));
            const tooltipDetail = [
              inSv2Window
                ? `Within SV2 visit window (${formatProtocolDay(sv2WinStart)} through ${formatProtocolDay(sv2WinEnd)})`
                : baselineCollectable
                ? 'Pre-D1 day'
                : `Pre-baseline gap before D1; SV2 closed after ${formatDisplayDateShort(sv2WindowEndDate)}`,
              baselineCollectable ? 'B1 (pre-dose baseline) collectable — home tests before D1 count toward B1' : '',
              hasTr ? 'Training test' : '',
              hasLoc ? 'Localizer test' : '',
              homeCount > 0
                ? `${homeCount} home test${homeCount === 1 ? '' : 's'} ${baselineCollectable ? 'collected pre-D1 (B1 baseline)' : 'before randomization'}`
                : '',
            ].filter(Boolean);
            const cell = (
              <div
                className="relative box-border flex flex-col items-center justify-center gap-0.5"
                style={{
                  width: `${cellSize}px`, height: '16px',
                  background: inSv2Window ? '#fef3c7' : baselineCollectable ? '#fefce8' : '#f9fafb',
                  border: '1px solid #f3f4f6',
                  borderRadius: '1px',
                }}
              >
                {sv2Dots.length > 3 ? (
                  <div className="absolute inset-0 flex items-center justify-center text-[6px] font-extrabold" style={{ color: testTypeColors.home.dot }}>
                    {sv2Dots.length}
                  </div>
                ) : sv2Dots.map((dot, di2) => (
                  <div
                    key={di2}
                    style={{
                      width: `${dot.size}px`, height: `${dot.size}px`,
                      borderRadius: dot.round ? '50%' : '0px',
                      background: dot.color,
                    }}
                  />
                ))}
              </div>
            );

            return (
              <ReportTooltip
                key={di}
                title={`${dateLabel} \u00b7 ${formatProtocolDay(dayNum)}`}
                detail={tooltipDetail}
                tone={inSv2Window || hasTr || hasLoc || homeCount > 0 ? 'warning' : 'default'}
              >
                {cell}
              </ReportTooltip>
            );
          })}
        </div>
        <div className="shrink-0 whitespace-nowrap flex items-center">
          <div className="shrink-0" style={{ width: '6px', height: '1px', background: '#cbd5e188' }} />
          <div className="text-[7px] text-gray-400 ml-1 min-w-[25px]">{formatDisplayDateShort(sv2EndDate)}</div>
          <div className="text-[8px] text-gray-500 font-mono ml-1">{formatProtocolDay(rowEnd)}</div>
        </div>
      </div>
      <div className="h-px bg-gray-200 my-0.5 ml-[95px]" />
    </>
  );
};

/** Per-bundle milestone row in the timeline grid */
const BundleMilestoneRow: React.FC<{
  milestone: BundleMilestone;
  milestoneIndex: number;
  milestones: BundleMilestone[];
  bundleStatus: BundleStatusInfo | null;
  baselineDate: Date;
  config: ProtocolConfig;
  getBundleStartWindow: (ms: BundleMilestone) => { start: number; target: number; end: number };
  getCollWindow: (ms: BundleMilestone) => { start: number; end: number } | null;
  testDayMap: Map<number, { total: number; outliers: number }>;
  assignedDayMap: Map<number, number>;
  assignedBundleCountsByDay: Map<number, AssignedBundleDayCount[]>;
  todayProtoDay: number;
  trainingDaySet: Set<number>;
  localizerDaySet: Set<number>;
  testCounts: BundleTestCount;
  referenceDate: Date;
  cellSize: number;
}> = ({
  milestone: ms, milestoneIndex: mi, milestones, bundleStatus, baselineDate, config,
  getBundleStartWindow, getCollWindow, testDayMap, assignedDayMap,
  assignedBundleCountsByDay, todayProtoDay, trainingDaySet, localizerDaySet, testCounts, referenceDate, cellSize,
}) => {
  const bundle = bundleStatus?.bundles.find(b => b.bundleNumber === ms.bundleNumber);
  const target = getBundleMilestoneProtocolDay(ms);
  // Pre-anchor baselines have no protocol target day — the operative date is
  // the D-1 deadline (baseline complete before dosing). Label the row with it
  // and draw no target marker; internals keep the D1 pin for ordering.
  const isPreAnchorRow = ms.windowMode === 'pre_anchor';
  const bundleLabel = `B${ms.bundleNumber}`;
  const nextBundleLabel = mi < milestones.length - 1 ? `B${milestones[mi + 1].bundleNumber}` : undefined;
  const vw = getBundleStartWindow(ms);
  const cw = getCollWindow(ms);
  const closeInfo = getBundleComplianceCloseInfo(config, ms, bundle, baselineDate);
  // ET-calendar-day compare — closeDate is a day boundary (noon-ET anchored)
  const collectionWindowClosed = Boolean(
    closeInfo.closeDate && differenceInETCalendarDays(referenceDate, closeInfo.closeDate) >= 0
  );

  const nextTarget = mi < milestones.length - 1 ? getBundleMilestoneProtocolDay(milestones[mi + 1]) : target + 84;
  const rowDays = nextTarget - target;
  const targetDate = addProtocolDays(baselineDate, target);

  // Status text — uses pre-computed test counts (single source of truth)
  const endProtocolDay = nextTarget - 1;
  const endDateLabel = formatDisplayDateShort(addProtocolDays(baselineDate, endProtocolDay));
  let statusText = '';
  if (bundle && bundle.testCount > 0) {
    statusText = `${testCounts.total}/${getBundleMilestoneTargetSessions(config, ms)}`;
  } else if (todayProtoDay > vw.end) {
    statusText = 'Missed';
  }
  const statusCellColor = bundleCountColor(
    testCounts.total,
    statusText === 'Missed',
    collectionWindowClosed,
    config,
    ms
  );

  return (
    <div className="flex items-center">
      {/* Left label — pre-anchor B1 shows its D-1 deadline, not a target day */}
      <div className="w-[95px] shrink-0 flex items-center justify-end">
        <div className="text-[10px] font-bold font-mono text-gray-700 w-[22px]">B{ms.bundleNumber}</div>
        <div className="text-[8px] text-gray-500 w-9 text-right font-mono">
          {isPreAnchorRow ? `≤${formatProtocolDay(-1)}` : formatProtocolDay(target)}
        </div>
        <div className="text-right text-[7px] text-gray-400 ml-1 min-w-[25px]">
          {formatDisplayDateShort(isPreAnchorRow ? addProtocolDays(baselineDate, -1) : targetDate)}
        </div>
        <div className="ml-auto shrink-0" style={{ width: '6px', height: '1px', background: '#4f46e588' }} />
      </div>

      {/* Day cells grid */}
      <div className="flex gap-px">
        {Array.from({ length: rowDays }).map((_, di) => {
          const dayNum = target + di;
          const isTarget = di === 0 && !isPreAnchorRow; // pre-anchor has no target day
          const isToday = dayNum === todayProtoDay;
          const dateLabel = formatDisplayDateShort(addProtocolDays(baselineDate, dayNum));

          const inStartWindow = dayNum >= vw.start && dayNum <= vw.end;
          let inNextStartWindow = false;
          if (mi < milestones.length - 1) {
            const nextVw = getBundleStartWindow(milestones[mi + 1]);
            if (dayNum >= nextVw.start && dayNum <= nextVw.end) inNextStartWindow = true;
          }

          const inColl = cw !== null && dayNum >= cw.start && dayNum <= cw.end;
          let inNextColl = false;
          if (mi < milestones.length - 1) {
            const nextCw = getCollWindow(milestones[mi + 1]);
            if (nextCw && dayNum >= nextCw.start && dayNum <= nextCw.end) inNextColl = true;
          }

          let bg = '#f9fafb';
          if (inColl || inNextColl) bg = '#dbeafe';
          else if (inStartWindow || inNextStartWindow) bg = '#fef3c7';

          let border = '1px solid #edf0f3';
          if (dayNum === todayProtoDay) border = '1.5px solid #f97316';
          else if (isTarget) border = '2px solid #4f46e5';

          return (
            <DayCell
              key={di}
              dayNum={dayNum}
              dateLabel={dateLabel}
              bundleLabel={bundleLabel}
              nextBundleLabel={nextBundleLabel}
              cellSize={cellSize}
              bg={bg}
              border={border}
              testInfo={testDayMap.get(dayNum)}
              assignedOnDay={assignedDayMap.get(dayNum) || 0}
              assignedBundleCounts={assignedBundleCountsByDay.get(dayNum) || []}
              isTarget={isTarget}
              isToday={isToday}
              inStartWindow={inStartWindow}
              inColl={inColl}
              inNextStartWindow={inNextStartWindow}
              inNextColl={inNextColl}
              hasTr={trainingDaySet.has(dayNum)}
              hasLoc={localizerDaySet.has(dayNum)}
            />
          );
        })}
      </div>

      {/* Right label — mirrors left: line · date · D### · status */}
      <div className="shrink-0 whitespace-nowrap flex items-center">
        <div className="shrink-0" style={{ width: '6px', height: '1px', background: '#cbd5e188' }} />
        <div className="text-[7px] text-gray-400 ml-1 min-w-[25px]">{endDateLabel}</div>
        <div className="text-[8px] text-gray-500 font-mono ml-1">{formatProtocolDay(endProtocolDay)}</div>
        <div
          className="text-[8px] ml-1.5"
          style={{
            color: statusCellColor.shouldColor ? statusCellColor.text : '#6b7280',
            fontWeight: statusCellColor.shouldColor ? 600 : 400,
            background: statusCellColor.shouldColor ? statusCellColor.bg : undefined,
            borderRadius: statusCellColor.shouldColor ? '3px' : undefined,
            padding: statusCellColor.shouldColor ? '0 3px' : undefined,
          }}
        >
          {statusText}
        </div>
      </div>
    </div>
  );
};

/** Axis annotation below the timeline. Always shows the Target marker. For
 * anchored, regularly-spaced protocols (e.g. SIENNA) it also shows the
 * collection-window edges on a reference row of the bundle-to-bundle spacing:
 * +{after}d after Target (the current bundle's window close) and -{before}d
 * (where the next bundle's start window opens). Values are derived from the
 * ruleset, so they track the protocol rather than a hard-coded 84/28/7. Skipped
 * for pre-anchor protocols (e.g. DFI18231) whose bundles have varying
 * spacing/windows, making a single fixed reference row meaningless. */
const TimelineAxis: React.FC<{ cellSize: number; config: ProtocolConfig }> = ({ cellSize, config }) => {
  const G = cellSize + 1;
  const milestones = config.bundles.milestones;
  const isPreAnchor = milestones[0]?.windowMode === 'pre_anchor';

  // Reference row = spacing between the first two day-scheduled milestones.
  // Window values come from the first post-baseline bundle (the baseline is
  // D1-only with a 0/0 window on SIENNA, so it can't supply them).
  const d0 = milestones[0]?.expectedDay;
  const d1 = milestones[1]?.expectedDay;
  const spacing = d0 != null && d1 != null ? d1 - d0 : null;
  const windowAfter = milestones[1] ? getBundleMilestoneWindowDaysAfter(config, milestones[1]) : null;
  const windowBefore = milestones[1] ? getBundleMilestoneWindowDaysBefore(config, milestones[1]) : null;

  const markers: { left: number; color: string; label: string }[] = [
    { left: 0, color: '#6366f1', label: 'Target' },
  ];
  let rowWidth = cellSize;
  if (
    !isPreAnchor &&
    spacing != null && spacing > 0 &&
    windowAfter != null && windowAfter > 0 &&
    windowBefore != null && windowBefore > 0
  ) {
    markers.push({ left: windowAfter * G, color: '#d97706', label: `+${windowAfter}d` });
    markers.push({ left: (spacing - windowBefore) * G, color: '#d97706', label: `-${windowBefore}d` });
    rowWidth = spacing * G;
  }

  return (
    <div className="flex items-start">
      <div className="w-[95px] shrink-0" />
      <div className="relative" style={{ width: `${rowWidth}px`, height: '16px' }}>
        {markers.map(({ left, color, label }) => (
          <div key={label} className="absolute top-0 flex flex-col items-center" style={{ left: `${left}px`, width: `${cellSize}px` }}>
            <div className="w-full h-[3px] rounded-sm" style={{ background: color }} />
            <span className="text-[6px] font-bold font-mono mt-px" style={{ color }}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================
// BUNDLE TIMELINE (orchestrator)
// ============================================================

interface BundleTimelineProps {
  bundleStatus: BundleStatusInfo | null;
  baselineDate: Date | null;
  config: ProtocolConfig;
  homeTimestamps: number[];
  trainingTimestamps: number[];
  localizerTimestamps: number[];
  vvpResults: VvpResult[] | null;
  bundleTestCounts: BundleTestCount[];
  referenceDate: Date;
}

const BundleTimeline: React.FC<BundleTimelineProps> = ({ bundleStatus, baselineDate, config, homeTimestamps, trainingTimestamps, localizerTimestamps, vvpResults, bundleTestCounts, referenceDate }) => {
  if (!baselineDate) return <div className="text-[10px] text-gray-400">No baseline date</div>;

  const milestones = config.bundles.milestones;
  const CELL = 10;

  const timelineTimestamps = (() => {
    if (!vvpResults || vvpResults.length === 0) {
      return {
        training: trainingTimestamps,
        localizer: localizerTimestamps,
        home: homeTimestamps,
      };
    }

    const next = {
      training: [] as number[],
      localizer: [] as number[],
      home: [] as number[],
    };

    for (const result of vvpResults) {
      if (isArchivedResult(result)) continue;
      const timestamp = parseInt(result.test_unix_time, 10);
      if (Number.isNaN(timestamp)) continue;

      const type = getTestType(result);
      if (type === 'Training') next.training.push(timestamp);
      else if (type === 'Localizer') next.localizer.push(timestamp);
      else if (type === 'Home') next.home.push(timestamp);
    }

    return {
      training: next.training.length > 0 ? next.training.sort((a, b) => a - b) : trainingTimestamps,
      localizer: next.localizer.length > 0 ? next.localizer.sort((a, b) => a - b) : localizerTimestamps,
      home: next.home.length > 0 ? next.home.sort((a, b) => a - b) : homeTimestamps,
    };
  })();

  // Convert timestamp to protocol study day (Day 1 = baseline), using ET calendar dates.
  const toStudyDay = (ts: number): number => {
    return getProtocolDayFromOffset(differenceInETCalendarDays(fromUnixTime(ts), baselineDate));
  };

  const trainingDaySet = new Set(timelineTimestamps.training.map(ts => toStudyDay(ts)));
  const localizerDaySet = new Set(timelineTimestamps.localizer.map(ts => toStudyDay(ts)));

  // Build home test map: study day -> { total, outliers }
  const testDayMap = new Map<number, { total: number; outliers: number }>();
  for (const ts of timelineTimestamps.home) {
    const sd = toStudyDay(ts);
    const cur = testDayMap.get(sd) || { total: 0, outliers: 0 };
    cur.total++;
    testDayMap.set(sd, cur);
  }
  if (vvpResults) {
    for (const r of vvpResults) {
      if (isArchivedResult(r) || !isHomeResult(r)) continue;
      if (isOutlierResult(r)) {
        const ts = parseInt(r.test_unix_time, 10);
        if (!isNaN(ts)) {
          const sd = toStudyDay(ts);
          const cur = testDayMap.get(sd);
          if (cur) cur.outliers++;
        }
      }
    }
  }

  // Assigned test counts per day (to detect overflow)
  const assignedDayMap = new Map<number, number>();
  const assignedBundleDayMap = new Map<number, number[]>();
  const assignedBundleCountsByDay = new Map<number, AssignedBundleDayCount[]>();
  if (bundleStatus) {
    for (const b of bundleStatus.bundles) {
      const bundleDays: number[] = [];
      for (const ts of b.testTimestamps) {
        const sd = toStudyDay(ts);
        assignedDayMap.set(sd, (assignedDayMap.get(sd) || 0) + 1);

        const dayCounts = assignedBundleCountsByDay.get(sd) || [];
        const bundleCount = dayCounts.find(item => item.bundleNumber === b.bundleNumber);
        if (bundleCount) {
          bundleCount.count++;
        } else {
          dayCounts.push({ bundleNumber: b.bundleNumber, count: 1 });
        }
        assignedBundleCountsByDay.set(sd, dayCounts);

        bundleDays.push(sd);
      }
      assignedBundleDayMap.set(b.bundleNumber, bundleDays.sort((a, b) => a - b));
    }
    for (const dayCounts of assignedBundleCountsByDay.values()) {
      dayCounts.sort((a, b) => a.bundleNumber - b.bundleNumber);
    }
  }

  const allHomeDays = timelineTimestamps.home.map(ts => toStudyDay(ts)).sort((a, b) => a - b);
  const getBundleStartWindow = (ms: BundleMilestone) => getBundleStartWindowDays(config, ms);
  const getCollWindow = (ms: BundleMilestone) => getCollectionWindowDays(
    config,
    ms,
    allHomeDays,
    bundleStatus ? (assignedBundleDayMap.get(ms.bundleNumber) || []) : undefined
  );
  const todayProtoDay = toStudyDay(Math.floor(referenceDate.getTime() / 1000));

  return (
    <div className="flex flex-col gap-0.5">
      <TimelineLegend />

      <SV2ScreeningRow
        baselineDate={baselineDate}
        trainingTimestamps={timelineTimestamps.training}
        localizerTimestamps={timelineTimestamps.localizer}
        homeTimestamps={timelineTimestamps.home}
        toStudyDay={toStudyDay}
        cellSize={CELL}
        // Pre-anchor baseline (DFI B1): SV2 lands D-7..D-1 (window_days_before
        // hint from the ruleset) and the whole pre-D1 strip is collectable.
        {...(config.bundles.milestones[0]?.windowMode === 'pre_anchor'
          ? {
              sv2Window: { start: -(config.bundles.milestones[0].windowDaysBefore ?? 7), end: -1 },
              baselineCollectable: true,
            }
          : {})}
      />

      {milestones.map((ms, mi) => (
        <BundleMilestoneRow
          key={ms.bundleNumber}
          milestone={ms}
          milestoneIndex={mi}
          milestones={milestones}
          bundleStatus={bundleStatus}
          baselineDate={baselineDate}
          config={config}
          getBundleStartWindow={getBundleStartWindow}
          getCollWindow={getCollWindow}
          testDayMap={testDayMap}
          assignedDayMap={assignedDayMap}
          assignedBundleCountsByDay={assignedBundleCountsByDay}
          todayProtoDay={todayProtoDay}
          trainingDaySet={trainingDaySet}
          localizerDaySet={localizerDaySet}
          testCounts={bundleTestCounts[mi]}
          referenceDate={referenceDate}
          cellSize={CELL}
        />
      ))}

      <TimelineAxis cellSize={CELL} config={config} />
    </div>
  );
};

// ============================================================
// MODAL SUB-COMPONENTS
// ============================================================

const CohortChip: React.FC<{ cohort: 'A' | 'B' | null }> = ({ cohort }) => {
  if (!cohort) return null;

  const isB = cohort === 'B';
  return (
    <ReportTooltip
      title={`Cohort ${cohort}`}
      detail={isB ? 'Randomized on/after 15-Apr-2026.' : 'Randomized before 15-Apr-2026.'}
      tone={isB ? 'info' : 'default'}
    >
      <span
        className={`inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm text-[8px] font-bold leading-none ${
          isB
            ? 'bg-blue-900 text-white'
            : 'border border-gray-400 bg-white text-gray-600'
        }`}
      >
        {cohort}
      </span>
    </ReportTooltip>
  );
};

const LayoutCreatedValues: React.FC<{ values: string[] }> = ({ values }) => {
  if (values.length === 0) {
    return <span className="text-gray-300">—</span>;
  }

  return (
    <span className="text-[10px] text-gray-700 font-mono" style={{ fontVariantNumeric: 'tabular-nums' }}>
      {values.map((v, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="text-gray-400">, </span>}
          <span className={i > 0 ? 'text-gray-500' : ''}>{v}</span>
        </React.Fragment>
      ))}
    </span>
  );
};

const headerTooltip: Record<string, { title: string; detail: string | string[]; tone?: 'default' | 'info' | 'warning' | 'danger' }> = {
  Participant: {
    title: 'Participant',
    detail: 'Click a row to show the bundle timeline.',
    tone: 'info',
  },
  'Rand Date': {
    title: 'Randomization date',
    detail: 'Determines Cohort A or B.',
    tone: 'info',
  },
  Next: {
    title: 'Next bundle',
    detail: 'Next upcoming bundle in the protocol schedule.',
    tone: 'info',
  },
  'Next Start Window': {
    title: 'Next Bundle Start Window',
    detail: 'Bundle Start Window open → target day → Bundle Start Window close.',
    tone: 'info',
  },
  'SV2 Window': {
    title: 'Projected Visit 2 (SV2)',
    detail: 'Projected SV2 window: first day → last day. Server-projected from screening — available before randomization.',
    tone: 'info',
  },
  'SV3 (Baseline)': {
    title: 'Projected Visit 3 / Baseline (SV3)',
    detail: 'Projected SV3 (Baseline) window: first day → last day.',
    tone: 'info',
  },
  Cmt: {
    title: 'Comments',
    detail: 'Hover the 💬 in a participant row to read the site’s notes.',
    tone: 'info',
  },
  Img: {
    title: 'Retinal imaging',
    detail: [
      'Whether the required retinal images are uploaded (per eye: 1 FAF macula, 1 FAF ONH, 97 OCT B-scans).',
      "Green check = at least one eye complete · amber ! = partial · — = none. Hover a row's marker for per-eye counts.",
    ],
    tone: 'info',
  },
};

const withHeaderTooltip = (header: string, content: React.ReactElement): React.ReactElement => {
  const tooltip = headerTooltip[header];
  return tooltip ? (
    <ReportTooltip title={tooltip.title} detail={tooltip.detail} tone={tooltip.tone} placement="bottom">
      {content}
    </ReportTooltip>
  ) : content;
};

const useHorizontalScrollState = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const dragStartRef = useRef({ pointerId: 0, x: 0, scrollLeft: 0, dragging: false });
  const suppressClickRef = useRef(false);
  const [state, setState] = useState({ canScroll: false, hasLeftShadow: false, hasRightShadow: false });
  const [isDragging, setIsDragging] = useState(false);

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;

    const maxScrollLeft = Math.max(0, el.scrollWidth - el.clientWidth);
    const canScroll = maxScrollLeft > 1;
    const next = {
      canScroll,
      hasLeftShadow: canScroll && el.scrollLeft > 1,
      hasRightShadow: canScroll && el.scrollLeft < maxScrollLeft - 1,
    };

    setState(current => (
      current.canScroll === next.canScroll &&
      current.hasLeftShadow === next.hasLeftShadow &&
      current.hasRightShadow === next.hasRightShadow
        ? current
        : next
    ));
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const handleChange = () => update();
    const animationFrame = window.requestAnimationFrame(handleChange);
    el.addEventListener('scroll', handleChange, { passive: true });
    window.addEventListener('resize', handleChange);

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(handleChange);
      resizeObserver.observe(el);
      Array.from(el.children).forEach(child => resizeObserver?.observe(child));
    }

    handleChange();

    return () => {
      window.cancelAnimationFrame(animationFrame);
      el.removeEventListener('scroll', handleChange);
      window.removeEventListener('resize', handleChange);
      resizeObserver?.disconnect();
    };
  }, [update]);

  const handlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el || !state.canScroll || event.button !== 0) return;

    dragStartRef.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      scrollLeft: el.scrollLeft,
      dragging: false,
    };
  }, [state.canScroll]);

  const handlePointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const el = ref.current;
    const dragStart = dragStartRef.current;
    if (!el || dragStart.pointerId !== event.pointerId) return;

    const deltaX = event.clientX - dragStart.x;
    if (!dragStart.dragging && Math.abs(deltaX) < 4) return;

    if (!dragStart.dragging) {
      dragStart.dragging = true;
      suppressClickRef.current = true;
      el.setPointerCapture?.(event.pointerId);
      setIsDragging(true);
    }

    el.scrollLeft = dragStart.scrollLeft - deltaX;
    event.preventDefault();
    update();
  }, [update]);

  const stopDragging = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const el = ref.current;
    const dragStart = dragStartRef.current;
    if (!el || dragStart.pointerId !== event.pointerId) return;

    if (el.hasPointerCapture?.(event.pointerId)) {
      el.releasePointerCapture?.(event.pointerId);
    }
    dragStartRef.current = { pointerId: 0, x: 0, scrollLeft: 0, dragging: false };
    setIsDragging(false);
    update();
    if (suppressClickRef.current) {
      window.setTimeout(() => {
        suppressClickRef.current = false;
      }, 0);
    }
  }, [update]);

  const handleClickCapture = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!suppressClickRef.current) return;

    event.preventDefault();
    event.stopPropagation();
    suppressClickRef.current = false;
  }, []);

  return {
    ref,
    isDragging,
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onPointerUp: stopDragging,
    onPointerCancel: stopDragging,
    onClickCapture: handleClickCapture,
    ...state,
  };
};

const HorizontalScrollFrame: React.FC<{
  cue: string;
  className?: string;
  cueClassName?: string;
  children: React.ReactNode;
}> = ({ cue, className = '', cueClassName = '', children }) => {
  const {
    ref,
    canScroll,
    hasLeftShadow,
    hasRightShadow,
    isDragging,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onClickCapture,
  } = useHorizontalScrollState();
  const frameClassName = [
    'sr-scroll-frame',
    className,
    canScroll ? 'sr-scrollable' : '',
    isDragging ? 'sr-dragging' : '',
    hasLeftShadow ? 'sr-scroll-left' : '',
    hasRightShadow ? 'sr-scroll-right' : '',
  ].filter(Boolean).join(' ');
  const cueClasses = [
    'sr-scroll-cue',
    cueClassName,
    canScroll ? 'sr-scroll-cue-visible' : '',
  ].filter(Boolean).join(' ');

  return (
    <>
      <div className={cueClasses} aria-hidden="true">{cue}</div>
      <div
        ref={ref}
        className={frameClassName}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onClickCapture={onClickCapture}
      >
        {children}
      </div>
    </>
  );
};

/** Bundle status table showing all participants in a compact grid */
/** Stacked projected-visit window cell: first day with last day beneath
 *  (e.g. "08-Jul-2026 / → 05-Aug-2026"). Mirrors LayoutCreatedValues styling so
 *  it reads consistently in the BSR's print/PDF-oriented table (plain tailwind,
 *  not Chakra). */
const ProjectedWindowCell: React.FC<{ first: Date | null; last: Date | null }> = ({ first, last }) => {
  if (!first && !last) {
    return <span className="text-gray-300">&mdash;</span>;
  }
  return (
    <span className="inline-flex flex-col font-mono text-[10px] leading-tight text-gray-700" style={{ fontVariantNumeric: 'tabular-nums' }}>
      <span>{first ? fmtDateDMY(first) : <span className="text-gray-300">&mdash;</span>}</span>
      {last ? <span className="text-[9px] text-gray-400">&rarr; {fmtDateDMY(last)}</span> : null}
    </span>
  );
};
ProjectedWindowCell.displayName = 'ProjectedWindowCell';

/** Comment indicator: a 💬 shown only when the participant has notes; the full
 *  (multi-line, dated) comment is revealed on hover, one entry per line. */
const CommentIndicatorCell: React.FC<{ comment: string | null }> = ({ comment }) => {
  const trimmed = comment?.trim();
  if (!trimmed) return <span className="text-gray-300">&mdash;</span>;
  const lines = trimmed.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
  return (
    <ReportTooltip title="Comment" detail={lines} tone="info" placement="left" maxW="320px">
      <span role="img" aria-label="Has comment" className="cursor-help">💬</span>
    </ReportTooltip>
  );
};
CommentIndicatorCell.displayName = 'CommentIndicatorCell';

/** Retinal-imaging status marker: ✓ when at least one eye has all required
 *  images (thresholds shared with the tracker's ImagesStatusCell), ! when
 *  images exist but neither eye is complete, — when none uploaded. Hover shows
 *  per-eye FAF / FAF-ONH / OCT counts. Plain tailwind for the print/PDF table. */
const ImagingStatusIndicatorCell: React.FC<{ counts: RetinalImageCounts | null }> = ({ counts }) => {
  if (!counts || Object.keys(counts).length === 0) {
    return (
      <ReportTooltip title="Retinal imaging" detail="No retinal images uploaded." tone="default" placement="left">
        <span className="text-gray-300 cursor-help">&mdash;</span>
      </ReportTooltip>
    );
  }
  const eyeReady = (eye: 'OD' | 'OS') => {
    const c = counts[eye];
    if (!c) return false;
    return (c.FAF ?? 0) >= REQUIRED_IMAGES.FAF
      && (c.FAFONH ?? 0) >= REQUIRED_IMAGES.FAFONH
      && (c.OCT ?? 0) >= REQUIRED_IMAGES.OCT;
  };
  const eyeLine = (eye: 'OD' | 'OS') =>
    `${eye} (${eyeReady(eye) ? 'Ready' : 'Incomplete'}): FAF ${counts[eye]?.FAF ?? 0}/${REQUIRED_IMAGES.FAF} · FAF-ONH ${counts[eye]?.FAFONH ?? 0}/${REQUIRED_IMAGES.FAFONH} · OCT ${counts[eye]?.OCT ?? 0}/${REQUIRED_IMAGES.OCT}`;
  const isReady = eyeReady('OD') || eyeReady('OS');
  return (
    <ReportTooltip title="Retinal imaging" detail={[eyeLine('OD'), eyeLine('OS')]} tone={isReady ? 'info' : 'warning'} placement="left" maxW="340px">
      {isReady ? (
        <span
          role="img"
          aria-label="Imaging ready"
          className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-green-100 text-green-700 cursor-help align-middle"
        >
          <svg viewBox="0 0 12 12" className="h-2.5 w-2.5" aria-hidden="true">
            <path d="M2.5 6.5 5 9l4.5-5.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      ) : (
        <span
          role="img"
          aria-label="Imaging incomplete"
          className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-amber-100 text-amber-700 text-[9px] font-bold leading-none cursor-help align-middle"
        >
          !
        </span>
      )}
    </ReportTooltip>
  );
};
ImagingStatusIndicatorCell.displayName = 'ImagingStatusIndicatorCell';

const OverviewTable: React.FC<{
  reportData: ReportParticipant[];
  milestones: BundleMilestone[];
  config: ProtocolConfig;
  referenceDate: Date;
  getId: (rd: ReportParticipant) => string;
  expandedParticipantId: string | null;
  onToggleExpanded: (rd: ReportParticipant) => void;
  renderExpandedContent: (rd: ReportParticipant) => React.ReactNode;
  sortConfig: SortConfig;
  onSort: (column: SortableColumn) => void;
  showProjectedVisitColumns: boolean;
  showImagingColumn: boolean;
  topContent?: React.ReactNode;
}> = ({ reportData, milestones, config, referenceDate, getId, expandedParticipantId, onToggleExpanded, renderExpandedContent, sortConfig, onSort, showProjectedVisitColumns, showImagingColumn, topContent }) => (
  <div className="sr-table-shell mb-6">
    <HorizontalScrollFrame cue="Scroll table horizontally" cueClassName="sr-table-scroll-cue" className="sr-table-scroll">
    {topContent}
    <table className="sr-overview-table w-full border-collapse text-[10px]" style={{ tableLayout: 'auto', minWidth: '1280px' }}>
      <thead>
        <tr>
          {['Participant', 'Investigator Name', 'Device', 'Last Login', 'Rand Date', ...(showProjectedVisitColumns ? ['SV2 Window', 'SV3 (Baseline)', 'Cmt'] : []), ...(showImagingColumn ? ['Img'] : []), 'Tr. Created', 'Loc. Created', 'Home Created'].map(h => {
            const sortCol = SORTABLE_COLUMN_MAP[h];
            const isActive = sortCol !== undefined && sortConfig.column === sortCol;
            const arrow = isActive ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : '';
            const headerContent = h === 'Participant' ? (
                <span className="inline-flex items-center">
                  <span className="inline-block w-3.5 shrink-0" aria-hidden="true" />
                  {h}{arrow}
                </span>
              ) : h === 'Rand Date' ? (
                <span className="inline-block" style={{ paddingLeft: '20px' }}>
                  {config.metadata.anchorLabel ?? h}{arrow}
                </span>
              ) : <span>{h}{arrow}</span>;
            return (
              <th
                key={h}
                className={`sticky top-0 z-10 ${h === 'Img' ? 'text-center' : 'text-left'} px-1.5 py-1 text-[9px] font-bold text-white bg-gray-600 border-b-2 border-gray-800 whitespace-nowrap`}
                style={sortCol ? { cursor: 'pointer', userSelect: 'none' } : undefined}
                onClick={sortCol ? () => onSort(sortCol) : undefined}
              >
                {withHeaderTooltip(h, headerContent)}
              </th>
            );
          })}
          {milestones.map(ms => {
            const isB1 = ms.bundleNumber === 1;
            const protocolDay = getBundleMilestoneProtocolDay(ms);
            return (
            <th
              key={ms.bundleNumber}
              className="sticky top-0 z-10 text-center px-1 py-1 text-[9px] font-bold text-white bg-gray-600 border-b-2 border-gray-800 whitespace-nowrap"
              style={{
                width: isB1 ? '54px' : '44px',
                minWidth: isB1 ? '54px' : '44px',
                background: isB1 ? B1_TINT : undefined,
                borderColor: isB1 ? B1_TINT_BORDER : undefined,
                color: isB1 ? '#374151' : undefined,
              }}
            >
              <ReportTooltip
                title={`B${ms.bundleNumber}`}
                detail={isB1 && ms.windowMode === 'pre_anchor'
                  ? ['Baseline is collected BEFORE Day 1 (dosing); the window closes at D1.', 'Home tests on any pre-D1 day count. Max 2 counted per day.', 'Marked as missed if D1 arrives with zero pre-D1 home tests.']
                  : isB1
                  ? ['Bundle Start opportunity is only on Day 1.', 'If started, Collection Window is 10 days or 10 tests. Max 2 counted per day.', 'Marked as missed if Day 1 closes with zero home tests.']
                  : [`Targets protocol Day ${protocolDay}.`, `Bundle Start Window is active from ${getBundleMilestoneWindowDaysBefore(config, ms)} days before through ${getBundleMilestoneWindowDaysAfter(config, ms) ?? '—'} days after the target day.`, `If started, Collection Window is 10 calendar days or 10 home tests. Max 2 counted per day.`]
                }
                tone="info"
                placement="bottom"
              >
                <span className="inline-flex flex-col items-center leading-none">
                  <span>B{ms.bundleNumber}</span>
                  {isB1 ? (
                    <span className="mt-0.5 font-sans text-[7px] font-medium text-gray-500">
                      {ms.windowMode === 'pre_anchor' ? '(pre-D1)' : '(D1 only)'}
                    </span>
                  ) : null}
                </span>
              </ReportTooltip>
            </th>
          );
          })}
          {['Next', 'Next Start Window'].map(h => {
            const sortCol = SORTABLE_COLUMN_MAP[h];
            const isActive = sortCol !== undefined && sortConfig.column === sortCol;
            const arrow = isActive ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : '';
            const isNextWindow = h === 'Next Start Window';
            return (
              <th
                key={h}
                className="sticky top-0 z-10 text-left px-1.5 py-1 text-[9px] font-bold text-white bg-gray-600 border-b-2 border-gray-800 whitespace-nowrap"
                style={{ ...(sortCol ? { cursor: 'pointer', userSelect: 'none' } : {}), ...(isNextWindow ? { minWidth: '120px' } : {}) }}
                onClick={sortCol ? () => onSort(sortCol) : undefined}
              >
                {withHeaderTooltip(h, <span>{h}{arrow}</span>)}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {reportData.map((rd, i) => {
          const rowId = rd.participant.patient_id || rd.participant.Subject || String(i);
          const isExpanded = expandedParticipantId === rowId;

          return (
            <React.Fragment key={rowId}>
              <tr
                className="sr-no-break"
                style={{ background: i % 2 === 0 ? '#fff' : '#fafafa', cursor: 'pointer' }}
                onClick={() => onToggleExpanded(rd)}
              >
              <td className="px-1.5 py-1 border-b border-gray-200 align-middle text-[10px] leading-tight font-semibold font-mono whitespace-nowrap" style={{ minWidth: '100px' }}>
              <span className="inline-block w-3 text-gray-400">{isExpanded ? '▾' : '▸'}</span>
              {getId(rd)}
              </td>
            <td className="px-1.5 py-1 border-b border-gray-200 align-middle text-[10px] leading-tight">
              {rd.participant.Investigator || <span className="text-gray-300">&mdash;</span>}
            </td>
            <td className="px-1.5 py-1 border-b border-gray-200 align-middle text-[10px] font-medium font-mono leading-tight whitespace-nowrap">
              {rd.deviceId}
            </td>
            <td className="px-1.5 py-1 border-b border-gray-200 align-middle text-[10px] font-mono leading-tight whitespace-nowrap">
              {rd.lastLogin}
            </td>
            <td className="px-1.5 py-1 border-b border-gray-200 align-middle text-[10px] font-mono leading-tight">
              <span className="flex items-center gap-1.5 whitespace-nowrap">
                <CohortChip cohort={rd.cohort} />
                {rd.randDate ? fmtDateDMY(rd.randDate) : <span className="text-gray-300">&mdash;</span>}
              </span>
            </td>
            {showProjectedVisitColumns && (
              <>
                <td className="px-1.5 py-1 border-b border-gray-200 align-middle text-[10px] leading-tight whitespace-nowrap">
                  <ProjectedWindowCell
                    first={parseApiDate(rd.participant['Projected Visit 2 First Day'])}
                    last={parseApiDate(rd.participant['Projected Visit 2 Last Day'])}
                  />
                </td>
                <td className="px-1.5 py-1 border-b border-gray-200 align-middle text-[10px] leading-tight whitespace-nowrap">
                  <ProjectedWindowCell
                    first={parseApiDate(rd.participant['Projected Baseline First Day'])}
                    last={parseApiDate(rd.participant['Projected Baseline Last Day'])}
                  />
                </td>
                <td className="px-1.5 py-1 border-b border-gray-200 align-middle text-center text-[10px] leading-tight">
                  <CommentIndicatorCell comment={rd.participant.Comments} />
                </td>
              </>
            )}
            {showImagingColumn && (
              <td className="px-1.5 py-1 border-b border-gray-200 align-middle text-center text-[10px] leading-tight">
                <ImagingStatusIndicatorCell counts={rd.participant.retinal_image_counts} />
              </td>
            )}
            <td className="px-1.5 py-0.5 border-b border-gray-200 align-middle text-[10px] leading-tight">
              <LayoutCreatedValues values={rd.layoutDays.training} />
            </td>
            <td className="px-1.5 py-0.5 border-b border-gray-200 align-middle text-[10px] leading-tight">
              <LayoutCreatedValues values={rd.layoutDays.localizer} />
            </td>
            <td className="px-1.5 py-0.5 border-b border-gray-200 align-middle text-[10px] leading-tight">
              <LayoutCreatedValues values={rd.layoutDays.home} />
            </td>
            {rd.bundleDescriptions.map((bd, bi) => {
              if (!rd.randDate) {
                const milestone = milestones[bi];
                return (
                  <td
                    key={bi}
                    className="px-1 py-1 border-b border-gray-200 align-middle text-center font-mono text-[9px] leading-tight text-gray-300"
                    style={{
                      width: milestone.bundleNumber === 1 ? '54px' : '44px',
                      minWidth: milestone.bundleNumber === 1 ? '54px' : '44px',
                      background: milestone.bundleNumber === 1 ? B1_TINT : undefined,
                      boxShadow: milestone.bundleNumber === 1 ? 'inset 2px 0 0 rgba(30, 64, 175, 0.18)' : undefined,
                    }}
                  >
                    &mdash;
                  </td>
                );
              }

              const tc = rd.bundleTestCounts[bi];
              const milestone = milestones[bi];
              const bundle = rd.bundleStatus?.bundles.find(b => b.bundleNumber === milestone.bundleNumber);
              const milestoneTarget = getBundleMilestoneTargetSessions(config, milestone);
              const closeInfo = getBundleComplianceCloseInfo(config, milestone, bundle, rd.randDate);
              // ET-calendar-day compare — closeDate is a day boundary (noon-ET anchored)
              const collectionWindowClosed = Boolean(
                closeInfo.closeDate && differenceInETCalendarDays(referenceDate, closeInfo.closeDate) >= 0
              );
              const cc = bundleCountColor(tc.total, bd.missed, collectionWindowClosed, config, milestone);
              const shouldColor = cc.shouldColor;
              const startWindowStart = rd.randDate
                ? addDays(rd.randDate, getBundleMilestoneWindowStartDay(config, milestone))
                : null;
              const startWindowEndDay = getBundleMilestoneWindowEndDay(config, milestone);
              const startWindowEnd = rd.randDate
                ? addDays(rd.randDate, startWindowEndDay ?? getBundleMilestoneExpectedDay(milestone))
                : null;
              const isOpenPartial = Boolean(
                tc.total > 0 &&
                tc.total < milestoneTarget &&
                !bd.missed &&
                closeInfo.closeDate &&
                !collectionWindowClosed
              );
              const isStartWindowOpenEmpty = Boolean(
                tc.total === 0 &&
                !bd.missed &&
                startWindowStart &&
                startWindowEnd &&
                referenceDate >= startWindowStart &&
                referenceDate <= startWindowEnd
              );
              let cellTooltipTitle: string | undefined;
              let cellTooltipDetail: string[] | undefined;
              let cellTooltipTone: 'info' | 'warning' | 'danger' = 'info';
              // User-facing copy shows the LAST OPEN day, not the (exclusive)
              // close boundary — "open through Jun 22" for a window whose last
              // collectable day is Jun 21 was off by one (found in DFI UAT).
              const lastOpenLabel = closeInfo.lastOpenDate ? formatDisplayDateShort(closeInfo.lastOpenDate) : null;
              const startWindowStartLabel = startWindowStart ? formatDisplayDateShort(startWindowStart) : null;
              const startWindowEndLabel = startWindowEnd ? formatDisplayDateShort(startWindowEnd) : null;
              const startWindowRangeLabel = startWindowStartLabel && startWindowEndLabel
                ? startWindowStartLabel === startWindowEndLabel
                  ? `Bundle Start Window: ${startWindowStartLabel} only.`
                  : `Bundle Start Window: ${startWindowStartLabel} through ${startWindowEndLabel}.`
                : null;
              if (isOpenPartial && lastOpenLabel) {
                cellTooltipTitle = `${tc.total}/${milestoneTarget} collected`;
                cellTooltipDetail = [`Collection Window open through ${lastOpenLabel}.`, 'Collection Window still open — count may change.'];
                cellTooltipTone = 'info';
              } else if (isStartWindowOpenEmpty && startWindowEnd) {
                cellTooltipTitle = `Bundle Start Window open through ${formatDisplayDateShort(startWindowEnd)}`;
                cellTooltipDetail = [
                  startWindowRangeLabel,
                  'No tests collected yet. Testing should begin within this Bundle Start Window.',
                ].filter((detail): detail is string => Boolean(detail));
                cellTooltipTone = 'warning';
              } else if (bd.missed) {
                cellTooltipTitle = 'Missed';
                cellTooltipDetail = [
                  startWindowRangeLabel,
                  tc.total > 0
                    ? `${tc.total}/${milestoneTarget} collected while Collection Window was open.`
                    : lastOpenLabel
                    ? `0 tests collected through ${lastOpenLabel}.`
                    : '0 tests collected while Collection Window was open.',
                  'Bundle data cannot be recovered.',
                ].filter((detail): detail is string => Boolean(detail));
                cellTooltipTone = 'danger';
              } else if (
                tc.total > 0 &&
                tc.total < milestoneTarget &&
                collectionWindowClosed
              ) {
                cellTooltipTitle = `${tc.total}/${milestoneTarget} collected`;
                cellTooltipDetail = [
                  `Collection Window ended ${lastOpenLabel}.`,
                ];
                cellTooltipTone = 'info';
              }
              const cellContent = (
                <span className="inline-flex min-h-[10px] w-full items-center justify-center">
                  {bd.missed ? <span className="font-semibold">Missed</span>
                    : tc.total > 0 ? (
                      <span className="font-mono">
                        <strong>{tc.total}</strong>
                        <span className="text-gray-400 font-normal">/{milestoneTarget}</span>
                      </span>
                    ) : bd.text || ''}
                </span>
              );
              const bundleCell = (
                <td
                  key={bi}
                  className="px-1 py-1 border border-gray-200 align-middle text-center font-mono text-[9px] leading-tight"
                  style={{
                    background: shouldColor ? cc.bg : milestone.bundleNumber === 1 ? B1_TINT : undefined,
                    color: shouldColor ? cc.text : '#374151',
                    outline: isOpenPartial
                      ? '1.5px dashed #2563eb'
                      : isStartWindowOpenEmpty
                      ? '1.5px dotted #d97706'
                      : undefined,
                    outlineOffset: isOpenPartial || isStartWindowOpenEmpty ? '-2px' : undefined,
                    boxShadow: milestone.bundleNumber === 1 ? 'inset 2px 0 0 rgba(30, 64, 175, 0.18)' : undefined,
                    width: milestone.bundleNumber === 1 ? '54px' : '44px',
                    minWidth: milestone.bundleNumber === 1 ? '54px' : '44px',
                  }}
                >
                  {cellContent}
                </td>
              );

              return cellTooltipTitle ? (
                <ReportTooltip key={bi} title={cellTooltipTitle} detail={cellTooltipDetail} tone={cellTooltipTone}>
                  {bundleCell}
                </ReportTooltip>
              ) : bundleCell;
            })}
            <td className="px-1.5 py-1 border-b border-gray-200 align-middle text-[9px] font-semibold font-mono leading-tight">{rd.nextBundle}</td>
            <td className="px-1.5 py-1 border-b border-gray-200 align-middle text-[9px] leading-tight">{rd.nextWindow}</td>
              </tr>
              {isExpanded && (
                <tr className="sr-screen-expanded-row sr-no-break">
                  <td colSpan={milestones.length + 10 + (showProjectedVisitColumns ? 3 : 0) + (showImagingColumn ? 1 : 0)} className="sr-expanded-cell bg-white border-b border-gray-300 px-0 py-0">
                    <div className="sr-expanded-panel border-l-4 border-blue-900 bg-white">
                      <HorizontalScrollFrame cue="Scroll timeline horizontally" cueClassName="sr-expanded-scroll-cue" className="sr-expanded-scroll px-3 py-3">
                        {renderExpandedContent(rd)}
                      </HorizontalScrollFrame>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
        {reportData.length === 0 && (
          <tr>
            <td colSpan={milestones.length + 10 + (showProjectedVisitColumns ? 3 : 0) + (showImagingColumn ? 1 : 0)} className="bg-white px-4 py-12 text-center">
              <div className="text-[11px] text-gray-400">No participants to display.</div>
            </td>
          </tr>
        )}
      </tbody>
    </table>
    </HorizontalScrollFrame>
  </div>
);

/** Scrollable test log body with bottom shadow cue when overflowing */
const TestLogScrollBody: React.FC<{ testLog: TestLogEntry[] }> = ({ testLog }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScroll, setCanScroll] = useState(false);
  const [atBottom, setAtBottom] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const overflows = el.scrollHeight > el.clientHeight + 1;
    setCanScroll(overflows);
    setAtBottom(overflows && el.scrollTop + el.clientHeight >= el.scrollHeight - 2);
  }, []);

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkScroll, { passive: true });
    return () => el.removeEventListener('scroll', checkScroll);
  }, [checkScroll, testLog.length]);

  return (
    <div className="relative">
      <div ref={scrollRef} className="sr-test-log-body font-mono overflow-y-auto" style={{ maxHeight: '200px' }}>
        {testLog.map((t, ti) => {
          const tc = t.type === 'Home' ? testTypeColors.home.dot
            : t.type === 'Localizer' ? testTypeColors.localizer.dot
            : t.type === 'Training' ? testTypeColors.training.dot
            : '#6b7280';
          return (
            <div
              key={ti}
              className="flex gap-1.5 py-0.5 border-b border-gray-100 text-[9px]"
              style={{ color: t.isArchived ? '#d1d5db' : t.isOutlier ? '#dc2626' : '#374151' }}
            >
              <span className="shrink-0 w-[140px]">{t.dateTime}</span>
              <span className="shrink-0 w-[52px] font-semibold" style={{ color: t.isArchived ? '#d1d5db' : tc }}>{t.type}</span>
              <span className="shrink-0 w-8 text-center">{t.bundleNumber}</span>
              {t.isOutlier && <span className="text-red-600 font-semibold">Outlier</span>}
              {t.isArchived && <span className="text-gray-400">Archived</span>}
            </div>
          );
        })}
      </div>
      {canScroll && !atBottom && (
        <div
          className="sr-test-log-scroll-shadow pointer-events-none absolute bottom-0 left-0 right-0"
          style={{ height: '28px', background: 'linear-gradient(transparent, rgba(255,255,255,0.95))' }}
        />
      )}
    </div>
  );
};

/** Chronological test log */
const TestLogTable: React.FC<{ testLog: TestLogEntry[] }> = ({ testLog }) => {
  if (testLog.length === 0) return null;
  return (
    <div className="mb-2.5">
      <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">Test Log ({testLog.length})</div>
      {/* Sticky header row */}
      <div className="font-mono">
        <div className="flex gap-1.5 py-0.5 mb-0.5 border-b-2 border-gray-300 text-[8px] font-bold text-gray-500">
          <span className="shrink-0 w-[140px]">Date / Time (ET)</span>
          <span className="shrink-0 w-[52px]">Type</span>
          <span className="shrink-0 w-8 text-center">Bundle</span>
          <span>Flags</span>
        </div>
      </div>
      {/* Scrollable test log body with scroll shadow cue */}
      <TestLogScrollBody testLog={testLog} />
    </div>
  );
};

/** Per-participant detail page (header, metrics, timeline, test log, overrides) */
const ParticipantDetail: React.FC<{
  rd: ReportParticipant;
  config: ProtocolConfig;
  getId: (rd: ReportParticipant) => string;
  referenceDate: Date;
  embedded?: boolean;
}> = ({ rd, config, getId, referenceDate, embedded = false }) => {
  const id = getId(rd);

  return (
    <div className={embedded ? 'mt-0' : 'sr-page-break mt-5'}>
      {/* Detail Header */}
      <div className="flex items-center gap-2.5 mb-2 border-b-2 border-gray-200 pb-1.5">
        <span className="text-sm font-bold font-mono text-gray-900">{id}</span>
        <span className="text-[10px] text-gray-500">Site {rd.participant['Site Number'] || '—'}</span>
        <span className="text-[10px] text-gray-500">
          {config.metadata.anchorLabel ?? 'Rand Date (IRT)'}: {rd.randDate ? fmtDateDMY(rd.randDate) : <span className="text-red-600">Missing</span>}
        </span>
        <span className="text-[10px] text-gray-500">
          Compliance: {formatCompliance(rd.participantCompliance)}
        </span>
        <span className="text-[10px] text-gray-500">Eye: {rd.participant['Study Eye'] || '—'}</span>
      </div>

      {/* Bundle Metrics Strip */}
      <div className="flex items-center gap-2 mb-2">
        <div className="flex gap-0.5">
          {rd.bundleDescriptions.map((bd, bi) => {
            const tc = rd.bundleTestCounts[bi];
            const missed = bd.missed;
            const milestone = config.bundles.milestones[bi];
            const bundle = rd.bundleStatus?.bundles.find(b => b.bundleNumber === milestone.bundleNumber);
            const closeInfo = getBundleComplianceCloseInfo(config, milestone, bundle, rd.randDate);
            // ET-calendar-day compare — closeDate is a day boundary (noon-ET anchored)
            const collectionWindowClosed = Boolean(
              closeInfo.closeDate && differenceInETCalendarDays(referenceDate, closeInfo.closeDate) >= 0
            );
            const cc = bundleCountColor(tc.total, missed, collectionWindowClosed, config, milestone);
            const hasTests = tc.total > 0;
            const bg = cc.shouldColor ? cc.bg : hasTests ? '#fff' : '#f3f4f6';
            const fg = cc.shouldColor ? cc.text : hasTests ? '#4b5563' : '#9ca3af';
            const value = missed
              ? 'Missed'
              : hasTests
              ? `${tc.total}/${getBundleMilestoneTargetSessions(config, milestone)}`
              : '\u00a0';
            return (
              <div key={bi} className="flex flex-col items-center gap-px rounded relative" style={{ padding: '3px 5px', background: bg, minWidth: '42px', border: '1px solid #e5e7eb' }}>
                <span className="text-[8px] font-bold font-mono" style={{ color: fg }}>B{bi + 1}</span>
                <span className="text-[10px] font-bold font-mono relative" style={{ color: fg }}>
                  {value}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Bundle Timeline */}
      <div className="sr-no-break mb-3">
        <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">Bundle Timeline</div>
        <BundleTimeline
          bundleStatus={rd.bundleStatus}
          baselineDate={rd.baselineDate}
          config={config}
          homeTimestamps={rd.participant.home_test_unix_times || []}
          trainingTimestamps={rd.participant.training_test_unix_times || []}
          localizerTimestamps={rd.participant.localizer_test_unix_times || []}
          vvpResults={rd.participant.vvp_results}
          bundleTestCounts={rd.bundleTestCounts}
          referenceDate={referenceDate}
        />
      </div>

      {/* Test Log */}
      <TestLogTable testLog={rd.testLog} />

      {/* Overrides */}
      {rd.participant['Overrides Applied'] && (
        <div className="sr-no-break bg-amber-50 border border-amber-200 rounded p-2 mb-2.5">
          <div className="text-[9px] font-bold text-amber-800 mb-0.5 uppercase tracking-wide">Overrides</div>
          <div className="text-[9px] text-amber-800">{rd.participant['Overrides Applied']}</div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================

const BundleStatusReportContent: React.FC<BundleStatusReportProps & { protocolConfig: ProtocolConfig }> = ({
  participants,
  deviceMap,
  layoutMap,
  onClose,
  renderMode = 'page',
  enableExpandableRows = true,
  isUatEligible = false,
  sptSource = 'production',
  onSptSourceChange,
  onRefetchData,
  hideSiteFilter = false,
  siteScopeLabel,
  protocolConfig,
}) => {
  const [expandedParticipantId, setExpandedParticipantId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ReportTab>('randomized');
  // Extra-column gating (all checks fail closed).
  //
  // Projected SV2/SV3 window + comment columns, ALL required:
  //   1. SIENNA / Regeneron R3918-AMD-2326 (pharma1) only — never another trial
  //      (e.g. DFI18231), even if a different study is ever served on a -dev host.
  //   2. Screening tab only — where the upcoming SV2/SV3 windows are actionable.
  //   3. Audience: dev subdomain (vvp-sienna.seevividly.com resolves to
  //      'vvp-sienna', no '-dev'; test mirrors App.tsx's dev-banner check) OR a
  //      pharma user flag containing '_VV' (e.g. PHARMA1_VV) or '_CRC' (e.g.
  //      PHARMA1_CRC) — VV staff and CRCs see the columns on prod and dev
  //      alike; site/PV users remain dev-only.
  //
  // Imaging column: any tab, any study, but only *_VV* / *_CRC* flag holders —
  // imaging-pipeline status for the people who process imaging and design
  // layouts, not part of the site-facing report.
  const subdomain = useAppStore((s) => s.subdomain);
  const { flags: pharmaFlags } = usePharma();
  const isDevSubdomain = subdomain.includes('-dev') || subdomain.includes('react');
  const isVVFlagUser = (pharmaFlags || []).some((f) => f.includes('_VV'));
  const isCRCFlagUser = (pharmaFlags || []).some((f) => f.includes('_CRC'));
  const isPrivilegedViewer = isVVFlagUser || isCRCFlagUser;
  const isSiennaStudy =
    protocolConfig.metadata.studyName === 'SIENNA' ||
    protocolConfig.metadata.protocolId === 'R3918-AMD-2326';
  const showProjectedVisitColumns = isSiennaStudy && activeTab === 'screening' && (isDevSubdomain || isPrivilegedViewer);
  const showImagingColumn = isPrivilegedViewer;
  const [siteFilter, setSiteFilter] = useState('All');
  const [participantFilter, setParticipantFilter] = useState('');
  const [cohortFilter, setCohortFilter] = useState<CohortFilter>('All');
  const [sortConfig, setSortConfig] = useState<SortConfig>(DEFAULT_SORT);
  const [referenceDate, setReferenceDate] = useState(() => new Date());
  const [isPrintMode, setIsPrintMode] = useState(false);
  const config = useMemo(() => getReportConfig(protocolConfig, 'exact'), [protocolConfig]);

  // Always rebucket using our own window logic so test counts are accurate
  const reportBundleStatusMap = useMemo(() => {
    const map = new Map<string, BundleStatusInfo>();
    participants.forEach(pt => {
      if (!pt.patient_id) return;
      const rand = parseApiDate(getParticipantAnchorValue(pt));
      const baseline = rand;
      const rebucketed = rebucketHomeTestsToVisitWindows(config, pt.vvp_results, baseline, pt.home_test_unix_times || null);
      map.set(pt.patient_id, calculateBundleStatus(config, rebucketed.bundles, baseline, referenceDate, rebucketed.allActivityTimestamps));
    });
    return map;
  }, [participants, config, referenceDate]);

  const allReportData = useMemo(() => {
    return participants.map((pt, idx): ReportParticipant => {
      const bs = reportBundleStatusMap.get(pt.patient_id || '') ?? null;
      const randDate = parseApiDate(getParticipantAnchorValue(pt));
      const baselineDate = randDate;
      const devices = pt.patient_id ? (deviceMap[pt.patient_id] || []) : [];
      const category = getParticipantCategory(pt);
      const cohort = getCohort(randDate, config);

      // Compute test counts first (single source of truth for all views)
      const bundleTestCounts = config.bundles.milestones.map((ms): BundleTestCount => {
        const bundle = bs?.bundles.find(b => b.bundleNumber === ms.bundleNumber);
        if (!randDate || !bundle || !bundle.startDate) return { total: 0, outliers: 0, reliable: 0 };
        const cStartTs = bundle.startDate.getTime() / 1000;
        const cEndTs = addDays(bundle.startDate, config.bundles.windowDays).getTime() / 1000;
        return countTestsInWindow(pt.vvp_results, bundle.testTimestamps, cStartTs, cEndTs);
      });

      const bundleDescriptions = config.bundles.milestones.map((ms, i) => {
        const bundle = bs?.bundles.find(b => b.bundleNumber === ms.bundleNumber);
        return describeBundleForReport(bundle, ms, baselineDate, config, bundleTestCounts[i], referenceDate);
      });

      const quality = deriveOverallQuality(bs, bundleDescriptions);
      const lastLoginDate = getLastLoginDateForReport(devices);

      // Next bundle: find first milestone whose window end is still in the future
      let nextBundle = '—';
      let nextBundleNumber = Infinity;
      let nextWindowDate: Date | null = null;
      let nextWindow: React.ReactNode = '—';
      if (bs && baselineDate) {
        const referenceDay = differenceInETCalendarDays(referenceDate, baselineDate);
        const nextMs = config.bundles.milestones.find(ms => {
          const wEndDay = getBundleMilestoneWindowEndDay(config, ms);
          if (wEndDay === null) return true; // open-ended
          return wEndDay >= referenceDay;
        });
        if (nextMs) {
          const yr = nextMs.studyYear > 1 ? ` (Y${nextMs.studyYear})` : '';
          nextBundle = `B${nextMs.bundleNumber}${yr}`;
          nextBundleNumber = nextMs.bundleNumber;
          const wStart = addDays(baselineDate, getBundleMilestoneWindowStartDay(config, nextMs));
          const target = addDays(baselineDate, getBundleMilestoneExpectedDay(nextMs));
          nextWindowDate = target;
          const wEndDay = getBundleMilestoneWindowEndDay(config, nextMs);
          const wEnd = wEndDay !== null ? addDays(baselineDate, wEndDay) : null;
          if (nextMs.windowMode === 'pre_anchor' && wEnd) {
            // Pre-anchor baseline: collectable any day BEFORE the anchor — the
            // start ← target → end triplet would show the lookback bound as a
            // bogus year-less date (e.g. baseline minus 365 days).
            nextWindow = `By ${formatDisplayDateShort(wEnd)} (pre-D1)`;
          } else if (wEnd) {
            nextWindow = (
              <span className="inline-flex items-baseline whitespace-nowrap font-mono">
                <span className="inline-block text-right" style={{ width: '42px' }}>{formatDisplayDateShort(wStart)}</span>
                <span className="inline-block text-center" style={{ width: '16px' }}>{'\u2190'}</span>
                <span className="inline-block text-center font-semibold" style={{ width: '42px' }}>{formatDisplayDateShort(target)}</span>
                <span className="inline-block text-center" style={{ width: '16px' }}>{'\u2192'}</span>
                <span className="inline-block text-left" style={{ width: '42px' }}>{formatDisplayDateShort(wEnd)}</span>
              </span>
            );
          } else {
            nextWindow = `Opens ${formatDisplayDateShort(wStart)}`;
          }
        } else if (bs.overallStatus === OverallBundleStatus.COMPLETE) {
          nextBundle = 'Done';
          nextWindow = '';
        }
      }

      const layoutData = pt.patient_id ? layoutMap[pt.patient_id] : undefined;
      const actionInfo = getActionableInfo(pt, layoutData, devices);
      const autoFlags = generateActionFlags(bs, bundleDescriptions);
      const actionFlags = [autoFlags, actionInfo.nextAction !== 'Monitor Home Bundle Progress' ? actionInfo.nextAction : ''].filter(Boolean).join('; ');
      const participantId = getParticipantIdentifier(pt);
      const complianceInput = { randDate, bundleStatus: bs, bundleTestCounts };

      return {
        participant: pt,
        bundleStatus: bs,
        randDate,
        baselineDate,
        category,
        cohort,
        siteLabel: getSiteLabel(pt),
        participantId,
        deviceId: getDeviceIdForReport(devices),
        lastLoginDate: lastLoginDate,
        lastLogin: lastLoginDate ? fmtDateTimeDMY(lastLoginDate) : '—',
        participantCompliance: calculateParticipantCompliance(complianceInput, config, referenceDate),
        quality,
        onboardingNotes: generateOnboardingNotes(pt, baselineDate, config),
        bundleDescriptions,
        bundleTestCounts,
        layoutDays: getLayoutDays(layoutMap, pt.patient_id, baselineDate),
        nextBundle,
        nextBundleNumber,
        nextWindowDate,
        nextWindow,
        actionFlags,
        testLog: buildTestLog(pt, bs),
        index: idx,
      };
    }).sort(sortReportParticipants);
  }, [participants, reportBundleStatusMap, config, deviceMap, layoutMap, referenceDate]);

  const siteOptions = useMemo(() => {
    return Array.from(new Set(
      allReportData
        .map(rd => rd.siteLabel)
        .filter(site => site && site !== '—')
    )).sort((a, b) => a.localeCompare(b));
  }, [allReportData]);

  const filteredBaseData = useMemo(() => {
    const participantQuery = participantFilter.trim().toLowerCase();
    return allReportData.filter(rd => {
      if (siteFilter !== 'All' && !rd.siteLabel?.toLowerCase().includes(siteFilter.toLowerCase())) return false;
      if (!participantQuery) return true;
      const searchable = [
        rd.participantId,
        rd.participant.Subject,
        rd.participant.patient_user_name,
        rd.participant.patient_id,
      ].filter(Boolean).join(' ').toLowerCase();
      return searchable.includes(participantQuery);
    });
  }, [allReportData, participantFilter, siteFilter]);

  const tabCounts = useMemo(() => {
    return filteredBaseData.reduce<Record<ReportTab, number>>(
      (acc, rd) => {
        acc[rd.category]++;
        return acc;
      },
      { randomized: 0, excluded: 0, screening: 0 }
    );
  }, [filteredBaseData]);

  const reportData = useMemo(() => {
    return filteredBaseData
      .filter(rd => rd.category === activeTab)
      .filter(rd => cohortFilter === 'All' || rd.cohort === cohortFilter)
      .sort(buildSortComparator(sortConfig));
  }, [activeTab, cohortFilter, filteredBaseData, sortConfig]);

  const complianceSummaries = useMemo(() => {
    // TODO(site-user role): should the compliance strip be hidden for site users? Spec is silent.
    // Pending decision from Shradha/Casey.
    return buildCohortComplianceSummary(
      allReportData.filter(rd => rd.category === 'randomized'),
      config,
      referenceDate
    );
  }, [allReportData, config, referenceDate]);

  const getId = (rd: ReportParticipant) => {
    return rd.participant.Subject || rd.participant.patient_user_name || '—';
  };

  const handleToggleExpanded = (rd: ReportParticipant) => {
    if (!enableExpandableRows) return;
    const rowId = rd.participant.patient_id || rd.participant.Subject || String(rd.index);
    setExpandedParticipantId(current => current === rowId ? null : rowId);
  };

  const reportCSS = `
    .sr-table-shell {
      min-width: 0;
      position: relative;
    }
    .sr-scroll-cue {
      display: none;
      align-items: center;
      justify-content: flex-end;
      height: 18px;
      padding: 0 2px 3px;
      color: #6b7280;
      font-size: 8px;
      font-weight: 700;
      letter-spacing: 0.04em;
      line-height: 1;
      text-transform: uppercase;
    }
    .sr-scroll-cue::after {
      content: '->';
      margin-left: 4px;
      color: #1f2937;
      font-size: 9px;
    }
    .sr-scroll-cue-visible {
      display: flex;
    }
    .sr-scroll-frame {
      --sr-scroll-bg: #fff;
      --sr-scroll-shadow: rgba(15, 23, 42, 0.18);
      min-width: 0;
      max-width: 100%;
      overflow-x: auto;
      overscroll-behavior-x: contain;
      scrollbar-width: thin;
      scrollbar-color: #9ca3af #f3f4f6;
      background-color: var(--sr-scroll-bg);
      transition: box-shadow 120ms ease;
    }
    .sr-scroll-frame.sr-scrollable {
      cursor: grab;
    }
    .sr-scroll-frame.sr-dragging {
      cursor: grabbing;
      user-select: none;
    }
    .sr-scroll-frame.sr-dragging * {
      cursor: grabbing !important;
      user-select: none;
    }
    .sr-scroll-frame.sr-scroll-left:not(.sr-scroll-right) {
      box-shadow: inset 12px 0 10px -12px var(--sr-scroll-shadow);
    }
    .sr-scroll-frame.sr-scroll-right:not(.sr-scroll-left) {
      box-shadow: inset -12px 0 10px -12px var(--sr-scroll-shadow);
    }
    .sr-scroll-frame.sr-scroll-left.sr-scroll-right {
      box-shadow:
        inset 12px 0 10px -12px var(--sr-scroll-shadow),
        inset -12px 0 10px -12px var(--sr-scroll-shadow);
    }
    .sr-scroll-frame::-webkit-scrollbar {
      height: 8px;
    }
    .sr-scroll-frame::-webkit-scrollbar-track {
      background: #f3f4f6;
    }
    .sr-scroll-frame::-webkit-scrollbar-thumb {
      background: #9ca3af;
      border-radius: 999px;
      border: 2px solid #f3f4f6;
    }
    .sr-expanded-cell {
      max-width: 0;
    }
    .sr-expanded-panel {
      contain: inline-size;
      max-width: 100%;
      min-width: 0;
    }
    .sr-expanded-scroll {
      --sr-scroll-bg: #fff;
      max-width: 100%;
      min-width: 0;
      overflow-y: visible;
    }
    .sr-table-scroll.sr-scrollable .sr-expanded-scroll {
      width: max-content;
      max-width: none;
      min-width: 100%;
      overflow-x: visible;
      box-shadow: none !important;
    }
    .sr-table-scroll.sr-scrollable .sr-expanded-scroll-cue {
      display: none !important;
    }
    .sr-print-only,
    .sr-print-summary {
      display: none;
    }
    .sr-print-root {
      display: none;
    }
    @media print {
      .sr-toolbar { display: none !important; }
      @page { size: landscape; margin: 0.3in; }
      * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      body {
        margin: 0 !important;
        background: #fff !important;
      }
      ${isPrintMode ? `
      body > *:not(.sr-print-root) {
        display: none !important;
      }
      .sr-print-root {
        display: block !important;
      }
      ` : ''}
      .sr-print-only,
      .sr-print-summary {
        display: block !important;
      }
      .sr-report-tooltip,
      .sr-screen-expanded-row,
      .sr-tab-bar,
      .sr-scroll-cue,
      .sr-test-log-scroll-shadow {
        display: none !important;
      }
      .sr-page-break { break-before: page; }
      .sr-no-break { break-inside: avoid; }
      .chakra-portal, .chakra-modal__overlay, .chakra-modal__content-container,
      .chakra-modal__content, .sr-page, .sr-body {
        position: static !important; overflow: visible !important; height: auto !important;
        max-height: none !important; display: block !important; inset: auto !important; transform: none !important;
      }
      .chakra-modal__overlay { background: none !important; }
      .sr-page {
        border: 0 !important;
        border-radius: 0 !important;
        min-height: 0 !important;
        position: static !important;
        width: 100% !important;
      }
      .sr-body { padding: 12px !important; }
      .sr-report-filter-bar {
        display: flex !important;
        align-items: stretch !important;
        gap: 8px !important;
        min-width: 0 !important;
        margin-bottom: 8px !important;
      }
      .sr-filter-controls {
        display: inline-flex !important;
        flex: 0 0 auto !important;
        min-width: 0 !important;
        padding: 4px 6px !important;
        background: #fff !important;
      }
      .sr-cohort-filter {
        display: flex !important;
      }
      .sr-source-filter,
      .sr-filter-divider,
      .sr-site-filter,
      .sr-participant-filter,
      .sr-reset-filter {
        display: none !important;
      }
      .sr-compliance-strip {
        display: grid !important;
        width: 100% !important;
        max-width: none !important;
      }
      .sr-scroll-frame {
        overflow: visible !important;
        scrollbar-gutter: auto !important;
        background: none !important;
      }
      .sr-overview-table { font-size: 8px !important; width: 100% !important; min-width: 0 !important; }
      .sr-overview-table th, .sr-overview-table td { padding: 3px 3px !important; font-size: 8px !important; }
      .sr-overview-table th { position: static !important; }
      .sr-overview-table tr { break-inside: avoid; }
      .sr-expanded-cell { max-width: none !important; }
      .sr-expanded-panel { contain: none !important; max-width: none !important; }
      .sr-expanded-scroll { overflow: visible !important; max-width: none !important; }
      .sr-test-log-body { max-height: none !important; overflow: visible !important; }
      .sr-print-detail {
        break-before: page;
        break-inside: avoid;
      }
    }
  `;

  const milestones = config.bundles.milestones;
  const generatedAtLabel = fmtDateTimeDMY(referenceDate);
  const handleClearFilters = useCallback(() => {
    setSiteFilter('All');
    setParticipantFilter('');
    setCohortFilter('All');
    setSortConfig(DEFAULT_SORT);
  }, []);
  const handleRefresh = useCallback(() => {
    setReferenceDate(new Date());
    onRefetchData?.();
  }, [onRefetchData]);
  const preparePrint = useCallback(() => {
    setIsPrintMode(true);
  }, []);
  const finishPrint = useCallback(() => {
    setIsPrintMode(false);
  }, []);
  const handlePrint = useCallback(() => {
    preparePrint();
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => window.print());
    });
  }, [preparePrint]);
  const handleHeaderSort = useCallback((column: SortableColumn) => {
    setSortConfig(current => {
      if (current.column !== column) return { column, direction: 'asc' };
      if (current.direction === 'asc') return { column, direction: 'desc' };
      return DEFAULT_SORT;
    });
  }, []);

  useEffect(() => {
    window.addEventListener('beforeprint', preparePrint);
    window.addEventListener('afterprint', finishPrint);
    return () => {
      window.removeEventListener('beforeprint', preparePrint);
      window.removeEventListener('afterprint', finishPrint);
    };
  }, [finishPrint, preparePrint]);

  const renderReportContent = (includePrintButton: boolean, includePrintDetails: boolean) => {
    const { header, filterBar } = buildReportTopSection({
      config,
      tabCounts,
      activeTab,
      onTabChange: setActiveTab,
      cohortFilter,
      onCohortChange: setCohortFilter,
      siteFilter,
      onSiteFilterChange: setSiteFilter,
      siteOptions,
      participantFilter,
      onParticipantFilterChange: setParticipantFilter,
      onClearFilters: handleClearFilters,
      sortConfig,
      complianceSummaries,
      lastRefresh: referenceDate,
      onRefresh: handleRefresh,
      onPrint: includePrintButton ? handlePrint : undefined,
      isUatEligible,
      sptSource,
      onSptSourceChange,
      hideSiteFilter,
      siteScopeLabel,
    });

    return (
      <>
        {header}
        <OverviewTable
          reportData={reportData}
          milestones={milestones}
          config={config}
          showProjectedVisitColumns={showProjectedVisitColumns}
          showImagingColumn={showImagingColumn}
          referenceDate={referenceDate}
          getId={getId}
          expandedParticipantId={expandedParticipantId}
          onToggleExpanded={handleToggleExpanded}
          renderExpandedContent={(rd) => (
            <ParticipantDetail rd={rd} config={config} getId={getId} referenceDate={referenceDate} embedded />
          )}
          sortConfig={sortConfig}
          onSort={handleHeaderSort}
          topContent={filterBar}
        />
        {includePrintDetails && reportData.length > 0 && (
          <div className="sr-print-only">
            {reportData.map(rd => {
              const rowId = rd.participant.patient_id || rd.participant.Subject || String(rd.index);
              return (
                <div key={rowId} className="sr-print-detail">
                  <ParticipantDetail rd={rd} config={config} getId={getId} referenceDate={referenceDate} embedded />
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  };

  const renderFooter = () => (
    <div className="mt-5 text-[9px] text-gray-400 border-t border-gray-200 pt-2">
      {config.metadata.studyName} · {config.metadata.protocolId} · {config.bundles.targetTestsPerBundle} tests/bundle · {config.bundles.windowDays}d collection · Generated {generatedAtLabel}
    </div>
  );

  const reportSurfaceClassName = `sr-page relative font-sans text-gray-900 bg-white flex flex-col ${renderMode === 'modal' ? 'h-screen' : 'min-h-[640px] border border-gray-200 rounded-xl overflow-hidden'}`;
  const printPortal = isPrintMode && typeof document !== 'undefined'
    ? createPortal(
      <div className="sr-print-root">
        <style>{reportCSS}</style>
        <div className={reportSurfaceClassName}>
          <div className="sr-body flex-1 overflow-auto p-5">
            {renderReportContent(false, true)}
            {renderFooter()}
          </div>
        </div>
      </div>,
      document.body
    )
    : null;

  return (
    <>
      <style>{reportCSS}</style>
      <div className={reportSurfaceClassName}>
        {renderMode === 'modal' && onClose ? (
          <button
            className="sr-toolbar absolute right-4 top-3 z-10 bg-white border border-gray-200 shadow-sm cursor-pointer text-xs text-gray-500 px-2.5 py-1 rounded flex items-center gap-1"
            onClick={onClose}
          >
            <span className="text-base leading-none">&times;</span> Close
          </button>
        ) : null}

        <div className="sr-body flex-1 overflow-auto p-5">
          {renderReportContent(true, false)}
          {renderFooter()}
        </div>
      </div>
      {printPortal}
    </>
  );
};

/** Gates the report on the server protocol config — renders nothing until the
 * ruleset has loaded so report math never runs against a missing config. */
export const BundleStatusReport: React.FC<BundleStatusReportProps> = props => {
  const { config } = useProtocolConfigContext();
  if (!config) return null;
  return <BundleStatusReportContent {...props} protocolConfig={config} />;
};

const SiteReportModal: React.FC<SiteReportModalProps> = ({
  isOpen, onClose, participants, bundleStatusMap, deviceMap, layoutMap,
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full">
      <ModalOverlay bg="rgba(0,0,0,0.85)" />
      <ModalContent m={0} borderRadius={0} boxShadow="none">
        <BundleStatusReport
          participants={participants}
          bundleStatusMap={bundleStatusMap}
          deviceMap={deviceMap}
          layoutMap={layoutMap}
          onClose={onClose}
          renderMode="modal"
        />
      </ModalContent>
    </Modal>
  );
};

export default SiteReportModal;
